import React, { useEffect, useRef, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  ArrowLeft, ArrowUpRight, Download, FileText, Sparkles, Shield, Users,
  TrendingUp, HeartPulse, LogOut, Clock, Layers, Compass, ArrowRight,
  Handshake, Lock, ArrowDown, LineChart, HelpCircle, Eye, Moon, Activity,
  Anchor as AnchorIcon, Info, Menu, X, BookOpen, Home, Wind, Projector,
} from "lucide-react";
import Logo from "../components/Logo";
import Doctrine from "../components/Doctrine";
import BiometricBridge from "../components/BiometricBridge";
import ProgramMindMap from "../components/ProgramMindMap";

/** Reveal on scroll */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("is-visible"); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    el.querySelectorAll(".reveal").forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
  return ref;
}


/* ============ SECTION 2: Nurse Experience ============ */
const landscape = [
  { icon: HeartPulse, title: "Emotional strain is widespread",
    text: "Nurses may experience exhaustion, moral distress, grief, sleep disruption, workplace pressure, and cumulative emotional strain across specialties and settings." },
  { icon: Clock, title: "The hardest moments often happen between formal touchpoints",
    text: "Support may be most needed after a difficult patient interaction, during a night shift, in the parking lot after work, or before returning the next day." },
  { icon: HelpCircle, title: "A nurse may not immediately know what is needed",
    text: "The need may be to be heard, regulate, reflect, prepare for a conversation, connect with a peer, or reach qualified human support." },
  { icon: Eye, title: "Recognition creates choice",
    text: "Earlier awareness can help a nurse identify what they are carrying before strain deepens into disconnection." },
];

/* ============ SECTION 3: Missing Awareness Layer ============ */
const generations = [
  { title: "Activity", body: "What a person did — steps, movement, calories, distance." },
  { title: "Physiology", body: "What the body may be signaling — heart rate, HRV, respiration, sleep quality, recovery, temperature." },
  { title: "Meaning", body: "What may be happening in the person's life — context, reflection, conversation, emotional awareness, and personal understanding." },
];

const comparison = [
  ["Education and wellbeing guidance", "Awareness at the moment support is needed"],
  ["Training and courses", "A private companion between formal programs"],
  ["Peer and leadership support", "Personalized navigation to relevant resources"],
  ["Surveys and workforce insight", "Voluntary understanding of emerging needs"],
  ["Organizational advocacy", "Privacy protected insight into broad patterns"],
];

/* ============ SECTION 4: Awareness Window (cascade) ============ */
const cascade = [
  "A difficult shift may create pressure.",
  "Unprocessed pressure may contribute to emotional exhaustion.",
  "Emotional exhaustion may deepen into moral distress or disconnection.",
  "THE AWARENESS WINDOW — Recognition creates more choices.",
  "Persistent moral distress may contribute to compassion fatigue.",
  "Compassion fatigue may increase burnout risk.",
  "Burnout may contribute to withdrawal, absence, or departure from the profession.",
];

/* ============ SECTION 10: Pilot ============ */
const pilotPhases = [
  { n: "Phase One", title: "Joint discovery",
    body: "Clarify priority nurse experiences, program connections, privacy boundaries, behavioral rules, and pilot success measures." },
  { n: "Phase Two", title: "Controlled pilot",
    body: "Offer a defined nurse population a limited experience over eight to twelve weeks." },
  { n: "Phase Three", title: "Evaluate and determine next steps",
    body: "Review trust, usefulness, engagement, resource connection, reported changes, privacy performance, safety performance, and the value of optional wearable integration. Then determine whether to refine, expand, pause, or conclude the initiative." },
];

const pilotRoles = [
  { icon: Handshake, label: "ANA",
    value: "Clinical guidance, program expertise, nurse participation, ethics and privacy review, evaluation, and governance." },
  { icon: Compass, label: "My Mood Groove",
    value: "Prototype development, experience design, behavioral implementation, soundscape integration, data safeguards, and pilot operations." },
  { icon: Users, label: "Participating nurses",
    value: "Voluntary use, direct feedback, experience testing, and guidance regarding language, relevance, trust, and usability." },
  { icon: Shield, label: "Independent reviewers",
    value: "Behavioral, clinical, privacy, legal, and technical review of pilot standards and outcomes." },
];

const pilotMeasurementGroups = [
  { title: "Experience outcomes", items: [
    "Trust and perceived privacy",
    "Usefulness and emotional relevance",
    "Usability and accessibility",
    "Engagement and return behavior",
  ]},
  { title: "Awareness outcomes", note: "Measures to be defined as pre/post self-report scales during joint discovery.", items: [
    "Reported awareness before and after an experience",
    "Change in clarity or emotional intensity",
    "Confidence regarding an appropriate next step",
    "Recognition of recurring patterns",
  ]},
  { title: "Program outcomes", items: [
    "Connection with relevant ANA resources",
    "Resource opening, saving, or completion",
    "Perceived relevance of recommended pathways",
    "Repeat engagement with ANA programs",
  ]},
  { title: "Governance outcomes", items: [
    "Safety performance",
    "Escalation appropriateness",
    "Consent comprehension",
    "Privacy control performance",
    "Bias and language review",
    "Optional wearable readiness",
  ]},
];

/* ============ SECTION 11: Ten Commitments ============ */
const foundationalCommitments = [
  "Privacy is the foundation of honesty.",
  "The person is more important than the algorithm.",
  "AI should amplify humanity, never replace compassion.",
];
const operatingPrinciples = [
  "Awareness comes before advice.",
  "Growth is understood across time.",
  "Physiology tells one story. Experience tells another.",
  "Every nurse's journey is unique.",
  "Reflection can strengthen awareness and support recovery.",
  "Technology should connect people with appropriate help.",
  "Awareness is the beginning of evolution.",
  "Organizational problems must not be disguised as individual shortcomings.",
];

/* ────────────────────────────────────────────────
   AWARENESS OVER TIME — private reflection dashboard
   ──────────────────────────────────────────────── */

const AOT_DATA = {
  "3M": {
    labels: ["Wk 1", "Wk 3", "Wk 5", "Wk 7", "Wk 9", "Wk 12"],
    awareness: [42, 50, 55, 63, 70, 78],
    stress: [70, 65, 60, 55, 48, 42],
    exhaustion: [55, 50, 42, 38, 30, 24],
  },
  "6M": {
    labels: ["DEC", "JAN", "FEB", "MAR", "APR", "MAY"],
    awareness: [35, 45, 55, 66, 80, 92],
    stress: [72, 68, 60, 58, 42, 30],
    exhaustion: [58, 52, 48, 40, 28, 18],
  },
  "12M": {
    labels: ["Jun", "Aug", "Oct", "Dec", "Feb", "Apr"],
    awareness: [28, 36, 44, 55, 72, 90],
    stress: [78, 74, 66, 60, 45, 32],
    exhaustion: [65, 58, 52, 44, 30, 20],
  },
};

const AOT_KEY_MOMENTS = [
  { icon: "💬", title: "Boundary-setting conversation", date: "Jan 14", body: "Felt more grounded and clear after a difficult talk." },
  { icon: "📅", title: "Appointment week stress", date: "Mar 3–7", body: "Stress peaked, but awareness remained steady." },
  { icon: "🌙", title: "Better sleep after evening reset", date: "Apr 18", body: "Evening routine shift improved rest and mood." },
];

/* Biometric focus — tapping a tile draws that metric's own trend line on both charts */
const AOT_BIOMETRIC_SERIES = {
  "3M": {
    sleep:    [55, 60, 66, 70, 74, 80],
    hrv:      [50, 55, 60, 64, 68, 74],
    recovery: [52, 58, 65, 71, 76, 84],
    stress:   [70, 65, 60, 55, 48, 42],
  },
  "6M": {
    sleep:    [48, 55, 62, 70, 78, 86],
    hrv:      [45, 52, 57, 62, 68, 74],
    recovery: [45, 55, 63, 72, 80, 88],
    stress:   [72, 68, 60, 58, 42, 30],
  },
  "12M": {
    sleep:    [40, 48, 56, 66, 76, 88],
    hrv:      [42, 48, 53, 60, 68, 76],
    recovery: [38, 50, 60, 70, 80, 90],
    stress:   [78, 74, 66, 60, 45, 32],
  },
};
const AOT_BIOMETRIC_META = {
  sleep:    { color: "#a5b4fc", label: "Sleep",    value: "7.2h",  trend: "+12%", note: "Rest recovery trend" },
  hrv:      { color: "#f9a8d4", label: "HRV",      value: "62 ms", trend: "+8%",  note: "Nervous system balance" },
  recovery: { color: "#6ee7b7", label: "Recovery", value: "78%",   trend: "+15%", note: "Restorative capacity" },
  stress:   { color: "#f87171", label: "Stress indicator",   value: "28",    trend: "-18%", note: "Load & recovery ratio" },
};

/* Themes constellation — grouped by cluster, laid out in a single SVG so nodes / labels / lines always align */
const AOT_THEMES = [
  // GROUND cluster (left) — internal stability
  { id: "self-trust", label: "Self-trust", cluster: "ground", x: 13, y: 16, count: 11, labelDy: -4.5, anchor: "middle", excerpt: "I trusted my instinct more this month, even when it was uncomfortable." },
  { id: "boundaries", label: "Boundaries", cluster: "ground", x: 28, y: 26, count: 14, labelDy: -4.5, anchor: "middle", excerpt: "Naming a boundary at work created unexpected relief." },
  { id: "rest",       label: "Rest",       cluster: "ground", x: 17, y: 40, count: 9,  labelDy:  5.5, anchor: "middle", excerpt: "The evening reset changed the shape of my mornings." },
  // GROWTH cluster (right) — outward evolution
  { id: "gratitude",  label: "Gratitude",  cluster: "growth", x: 68, y: 14, count: 12, labelDy: -4.5, anchor: "middle", excerpt: "Naming small good things shifted the whole shift." },
  { id: "purpose",    label: "Purpose",    cluster: "growth", x: 86, y: 26, count: 8,  labelDy: -4.5, anchor: "middle", excerpt: "I remembered why I came into this work." },
  { id: "letting-go", label: "Letting go", cluster: "growth", x: 74, y: 40, count: 7,  labelDy:  5.5, anchor: "middle", excerpt: "Releasing what wasn't mine to carry was the harder gift." },
  // STRAIN (center-bottom) — hard to hold themes
  { id: "overwhelm",  label: "Overwhelm",  cluster: "strain", x: 48, y: 42, count: 6,  labelDy:  5,   anchor: "middle", excerpt: "It came in waves, and it also passed in waves." },
];
const AOT_CLUSTER_COLOR = {
  ground: "#8ec4ff",
  growth: "#EED89A",
  strain: "#b4a5ff",
};
const AOT_EDGES = [
  // Ground internal
  ["self-trust", "boundaries", 0.9],
  ["boundaries", "rest", 0.75],
  ["self-trust", "rest", 0.5],
  // Growth internal
  ["gratitude", "purpose", 0.85],
  ["gratitude", "letting-go", 0.7],
  ["purpose", "letting-go", 0.55],
  // Bridges (ground ↔ growth)
  ["boundaries", "gratitude", 0.65],
  ["rest", "letting-go", 0.55],
  // Strain ties
  ["overwhelm", "boundaries", 0.5],
  ["overwhelm", "letting-go", 0.5],
];

// Build a smooth SVG path from a series of y-values (0..100), plotted across width W
function buildSmoothPath(values, W = 400, H = 160, pad = 8) {
  const innerW = W - pad * 2;
  const innerH = H - pad * 2;
  const points = values.map((v, i) => [
    pad + (i / (values.length - 1)) * innerW,
    pad + (1 - v / 100) * innerH,
  ]);
  if (points.length < 2) return "";
  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[i + 1];
    const cpx = (x0 + x1) / 2;
    d += ` C ${cpx} ${y0}, ${cpx} ${y1}, ${x1} ${y1}`;
  }
  return { path: d, points };
}

function AotLivePhone({ range, setRange, hoveredIndex, setHoveredIndex, data, syncPulse, focusedBiometric, toggleFocus, focusedSeries, focusedMeta }) {
  const W = 220, H = 96;
  const aware = buildSmoothPath(data.awareness, W, H);
  const stress = buildSmoothPath(data.stress, W, H);
  const exhaust = buildSmoothPath(data.exhaustion, W, H);
  const focusedPath = focusedSeries ? buildSmoothPath(focusedSeries, W, H) : null;
  const rangeText = range === "3M" ? "3-month" : range === "12M" ? "12-month" : "6-month";
  return (
    <div className="aot-phone-wrap" data-testid="aot-live-phone">
      {/* Ambient pedestal glow behind phone */}
      <div className="aot-phone-halo" aria-hidden />
      <div className={`aot-phone-frame ${syncPulse ? "aot-phone-sync" : ""}`} key={`phone-${syncPulse}`}>
        {/* Bezel side buttons — subtle */}
        <span className="aot-phone-btn aot-phone-btn-r" aria-hidden />
        <span className="aot-phone-btn aot-phone-btn-l1" aria-hidden />
        <span className="aot-phone-btn aot-phone-btn-l2" aria-hidden />
        <span className="aot-phone-btn aot-phone-btn-l3" aria-hidden />
        <div className="aot-phone-screen">
          {/* Dynamic Island */}
          <div className="aot-phone-notch" aria-hidden />
          {/* Status bar */}
          <div className="aot-phone-status">
            <span>9:41</span>
            <span className="aot-status-icons">
              <svg width="14" height="9" viewBox="0 0 14 9" fill="none"><rect x="0" y="6" width="2" height="3" rx="0.3" fill="#fff"/><rect x="3" y="4" width="2" height="5" rx="0.3" fill="#fff"/><rect x="6" y="2" width="2" height="7" rx="0.3" fill="#fff"/><rect x="9" y="0" width="2" height="9" rx="0.3" fill="#fff"/></svg>
              <svg width="12" height="9" viewBox="0 0 12 9" fill="none"><path d="M6 8.5c.6 0 1.1-.5 1.1-1.1 0-.6-.5-1.1-1.1-1.1s-1.1.5-1.1 1.1c0 .6.5 1.1 1.1 1.1zm-3.4-3.3l1.1 1.1c1.3-1.3 3.4-1.3 4.7 0l1.1-1.1c-1.9-1.9-5-1.9-6.9 0zm-2.3-2.3l1.1 1.1c2.6-2.6 6.7-2.6 9.3 0l1.1-1.1c-3.2-3.2-8.3-3.2-11.5 0z" fill="#fff"/></svg>
              <svg width="18" height="9" viewBox="0 0 18 9" fill="none"><rect x="0.5" y="0.5" width="14" height="8" rx="1.5" stroke="#fff" opacity="0.6"/><rect x="2" y="2" width="10" height="5" rx="0.5" fill="#fff"/><rect x="15" y="3" width="1.5" height="3" rx="0.4" fill="#fff" opacity="0.6"/></svg>
            </span>
          </div>
          {/* Screen content */}
          <div className="aot-phone-content">
            {/* App header */}
            <div className="text-center mb-2">
              <div className="text-[#EED89A] text-[8px] tracking-[0.15em] uppercase opacity-70">◈</div>
              <div className="text-white/95 font-heading text-[9px] tracking-[0.02em] leading-tight">My Mood Groove</div>
            </div>
            <div className="mb-1.5">
              <div className="text-white font-heading text-[13px] leading-tight">Awareness Over Time</div>
              <div className="text-[7.5px] text-[#EED89A]/70 leading-snug mt-0.5">Your {rangeText} reflective index of<br />emotional patterns and progress.</div>
            </div>
            {/* Mini range pills */}
            <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] p-[2px] mb-1.5" data-testid="aot-phone-range">
              {["3M","6M","12M"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRange(r)}
                  className={`px-2 py-[3px] rounded-full text-[8px] font-heading font-medium tracking-wide transition ${range===r ? "bg-gradient-to-b from-[#F4EAC8] to-[#E6D19E] text-[#0F1E44] shadow-[0_2px_6px_rgba(238,216,154,0.35)]" : "text-zinc-300"}`}
                  data-testid={`aot-phone-range-${r}`}
                >{r}</button>
              ))}
            </div>
            {/* Mini legend */}
            <div className="flex items-center gap-2 text-[6.5px] text-zinc-300/85 mb-1">
              <span className="inline-flex items-center gap-1"><span className="w-1.5 h-[1px] rounded-full bg-[#EED89A]" />Self-Reported</span>
              <span className="inline-flex items-center gap-1"><span className="w-1.5 h-[1px] rounded-full bg-[#6ea3ff]" />Perceived</span>
              <span className="inline-flex items-center gap-1"><span className="w-1.5 h-[1px] rounded-full bg-[#c5b0ff]" />Reported</span>
            </div>
            {/* Mini chart */}
            <div className="relative">
              <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto" className="block">
                {[0,50,100].map((g) => {
                  const y = 4 + (1 - g/100) * (H - 8);
                  return <line key={g} x1="0" y1={y} x2={W} y2={y} stroke="rgba(255,255,255,0.05)" strokeDasharray="1 3" />;
                })}
                <path d={exhaust.path} fill="none" stroke="#c5b0ff" strokeWidth="1.6" strokeLinecap="round" opacity={focusedBiometric ? 0.18 : 0.85} />
                <path d={stress.path} fill="none" stroke="#6ea3ff" strokeWidth="1.7" strokeLinecap="round" opacity={focusedBiometric ? 0.18 : 0.95} />
                <path d={aware.path} fill="none" stroke="#EED89A" strokeWidth="2" strokeLinecap="round" opacity={focusedBiometric ? 0.22 : 1} />
                {/* Focused biometric overlay */}
                {focusedPath && (
                  <g className="aot-focus-line" key={`phone-focus-${focusedBiometric}-${range}`}>
                    <path d={focusedPath.path} fill="none" stroke={focusedMeta.color} strokeWidth="2.4" strokeLinecap="round" />
                    {focusedPath.points.map(([px, py], i) => (
                      <circle key={i} cx={px} cy={py} r={i === focusedPath.points.length - 1 ? 2.8 : 1.8} fill={focusedMeta.color} stroke="#0e1d42" strokeWidth="0.9" />
                    ))}
                  </g>
                )}
                {/* End dots (base) */}
                {!focusedBiometric && [["#c5b0ff", exhaust.points], ["#6ea3ff", stress.points], ["#EED89A", aware.points]].map(([color, pts], i) => {
                  const [x, y] = pts[pts.length-1];
                  return <circle key={i} cx={x} cy={y} r="2.2" fill={color} stroke="#0e1d42" strokeWidth="1" />;
                })}
                {/* Synced scrubber */}
                {hoveredIndex !== null && (() => {
                  const [hx] = aware.points[hoveredIndex];
                  const [, hyA] = aware.points[hoveredIndex];
                  return (
                    <g className="aot-scrubber">
                      <line x1={hx} y1={4} x2={hx} y2={H-10} stroke="rgba(238,216,154,0.6)" strokeWidth="0.7" strokeDasharray="1.5 2" />
                      {focusedPath
                        ? <circle cx={focusedPath.points[hoveredIndex][0]} cy={focusedPath.points[hoveredIndex][1]} r="2.8" fill={focusedMeta.color} stroke="#fff" strokeWidth="0.7" className="aot-scrubber-pulse" />
                        : <circle cx={hx} cy={hyA} r="2.6" fill="#EED89A" stroke="#fff" strokeWidth="0.7" className="aot-scrubber-pulse" />
                      }
                    </g>
                  );
                })()}
                {/* x-axis labels */}
                {data.labels.map((lbl, i) => {
                  const x = (i/(data.labels.length-1)) * W;
                  const active = i === hoveredIndex;
                  return <text key={lbl} x={x} y={H-1} fill={active ? "rgba(238,216,154,0.95)" : "rgba(255,255,255,0.55)"} fontSize="5.5" textAnchor="middle" fontFamily="var(--font-body)">{lbl}</text>;
                })}
                {/* Invisible hitboxes to sync back to dashboard */}
                {data.labels.map((_, i) => {
                  const bx = (i / (data.labels.length - 1)) * W;
                  const bw = W / Math.max(1, data.labels.length - 1);
                  return (
                    <rect
                      key={`ph-hit-${i}`}
                      x={bx - bw / 2}
                      y={0}
                      width={bw}
                      height={H - 8}
                      fill="transparent"
                      onMouseEnter={() => setHoveredIndex(i)}
                      onMouseLeave={() => setHoveredIndex((v) => (v === i ? null : v))}
                      data-testid={`aot-phone-hit-${i}`}
                    />
                  );
                })}
              </svg>
            </div>
            {/* Emotional intensity gradient bar */}
            <div className="mt-1.5">
              <div className="flex items-center justify-between text-[5.5px] uppercase tracking-[0.14em] text-zinc-400 mb-0.5">
                <span>Low</span><span className="text-zinc-300">Emotional Intensity</span><span>High</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden border border-white/10 aot-intensity-bar" />
            </div>
            {/* Mini biometrics — tappable to focus a metric on both charts */}
            <div className="mt-2">
              <div className="text-[6px] uppercase tracking-[0.18em] text-[#EED89A]/70 mb-1">Biometrics · tap to focus</div>
              <div className="grid grid-cols-4 gap-1">
                {[
                  { key: "sleep",    label: "Sleep",    value: "7.2h", trend: "+12%" },
                  { key: "hrv",      label: "HRV",      value: "62ms", trend: "+8%"  },
                  { key: "recovery", label: "Recovery", value: "78%",  trend: "+15%" },
                  { key: "stress",   label: "Stress indicator",   value: "28",   trend: "-18%" },
                ].map((b) => {
                  const active = focusedBiometric === b.key;
                  const color = AOT_BIOMETRIC_META[b.key].color;
                  return (
                    <button
                      key={b.label}
                      type="button"
                      onClick={() => toggleFocus(b.key)}
                      className={`rounded-md border px-1 py-1 text-left transition ${active ? "" : "border-white/10 bg-white/[0.03]"}`}
                      style={active ? { borderColor: color, background: `linear-gradient(180deg, ${color}22, ${color}0a)`, boxShadow: `0 0 0 1px ${color}55` } : undefined}
                      data-testid={`aot-phone-bio-${b.key}`}
                      aria-pressed={active}
                    >
                      <div className="text-[5.5px] uppercase tracking-wider leading-none" style={{ color: active ? color : "rgba(228,228,231,0.85)" }}>{b.label}</div>
                      <div className="text-white font-heading text-[8.5px] leading-tight mt-0.5">{b.value}</div>
                      <div className="text-emerald-300/90 text-[5.5px] leading-none">{b.trend}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* CTA pill */}
            <div className="mt-2 flex items-center justify-center">
              <div className="rounded-full bg-gradient-to-b from-[#F4EAC8] to-[#E6D19E] text-[#0F1E44] font-heading font-medium text-[7px] tracking-wide px-3 py-1 shadow-[0_2px_6px_rgba(238,216,154,0.35)] w-[85%] text-center">
                Explore Key Moments →
              </div>
            </div>
            <div className="mt-1.5 flex items-center justify-center gap-1 text-[5.5px] uppercase tracking-[0.18em] text-zinc-400">
              <Lock size={6} className="text-[#EED89A]" /> Private. Personal. Protected.
            </div>
          </div>
          {/* Home indicator */}
          <div className="aot-phone-home" aria-hidden />
        </div>
      </div>
      {/* Reflection under pedestal */}
      <div className="aot-phone-reflection" aria-hidden />
    </div>
  );
}

function ThemeConstellation() {
  const [active, setActive] = useState(null);
  const [generated, setGenerated] = useState({}); // { [theme_id]: excerpt }
  const [pending, setPending] = useState({});     // { [theme_id]: true }
  const [failed, setFailed] = useState({});       // { [theme_id]: true }  — fell back to static
  const themeById = useMemo(() => Object.fromEntries(AOT_THEMES.map((t) => [t.id, t])), []);
  const API = process.env.REACT_APP_BACKEND_URL;

  const requestReflection = React.useCallback((theme_id) => {
    if (!theme_id || !API) return;
    if (generated[theme_id] || pending[theme_id]) return;
    setPending((p) => ({ ...p, [theme_id]: true }));
    setFailed((f) => { const n = { ...f }; delete n[theme_id]; return n; });

    // 4s hard timeout — never let the drawer sit on shimmer forever
    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort(), 4000);

    fetch(`${API}/api/ana/theme-reflection`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ theme_id }),
      signal: ctrl.signal,
    })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`http ${r.status}`)))
      .then((data) => {
        if (data && data.success && data.excerpt) {
          setGenerated((g) => ({ ...g, [theme_id]: data.excerpt }));
        } else {
          setFailed((f) => ({ ...f, [theme_id]: true }));
        }
      })
      .catch(() => {
        setFailed((f) => ({ ...f, [theme_id]: true }));
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setPending((p) => { const n = { ...p }; delete n[theme_id]; return n; });
      });
  }, [API, generated, pending]);

  const setActiveAndFetch = (theme_id) => {
    setActive(theme_id);
    if (theme_id) requestReflection(theme_id);
  };
  const regenerate = (theme_id) => {
    if (!theme_id) return;
    setGenerated((g) => { const n = { ...g }; delete n[theme_id]; return n; });
    setFailed((f) => { const n = { ...f }; delete n[theme_id]; return n; });
    requestReflection(theme_id);
  };
  const isConnected = (id) => {
    if (!active) return true;
    if (id === active) return true;
    return AOT_EDGES.some(([a, b]) => (a === active && b === id) || (b === active && a === id));
  };
  const isEdgeActive = ([a, b]) => !active || a === active || b === active;
  const activeTheme = active ? themeById[active] : null;
  const clusterCount = { ground: 0, growth: 0, strain: 0 };
  AOT_THEMES.forEach((t) => { clusterCount[t.cluster] += t.count; });
  return (
    <div className="relative">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-teal-300">Themes across your reflections</div>
          <div className="text-zinc-400 text-[11.5px] mt-1">Related themes may appear together across selected reflections. Thicker connections indicate themes that appeared together more frequently.</div>
        </div>
        {/* Cluster legend */}
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em]">
          <span className="inline-flex items-center gap-1.5 text-zinc-300"><span className="w-2 h-2 rounded-full" style={{ background: AOT_CLUSTER_COLOR.ground, boxShadow: `0 0 8px ${AOT_CLUSTER_COLOR.ground}` }} /> Stability · {clusterCount.ground}</span>
          <span className="inline-flex items-center gap-1.5 text-zinc-300"><span className="w-2 h-2 rounded-full" style={{ background: AOT_CLUSTER_COLOR.growth, boxShadow: `0 0 8px ${AOT_CLUSTER_COLOR.growth}` }} /> Growth · {clusterCount.growth}</span>
          <span className="inline-flex items-center gap-1.5 text-zinc-300"><span className="w-2 h-2 rounded-full" style={{ background: AOT_CLUSTER_COLOR.strain, boxShadow: `0 0 8px ${AOT_CLUSTER_COLOR.strain}` }} /> Strain · {clusterCount.strain}</span>
        </div>
      </div>

      <div
        className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-[#050b1e]/85 to-[#0a1530]/85 overflow-hidden shadow-inner"
        style={{ aspectRatio: "20 / 9" }}
        data-testid="aot-constellation"
        onMouseLeave={() => setActive(null)}
      >
        {/* Ambient starfield background */}
        <div className="absolute inset-0 pointer-events-none opacity-70">
          {Array.from({ length: 34 }).map((_, i) => {
            const left = (i * 17.7) % 100;
            const top  = (i * 31.3) % 100;
            const size = 1 + ((i * 7) % 3);
            const delay = ((i * 13) % 60) / 10;
            const dur   = 3 + ((i * 5) % 5);
            return <span key={i} className="aot-cn-bg-star" style={{ left: `${left}%`, top: `${top}%`, width: size, height: size, animationDuration: `${dur}s`, animationDelay: `${delay}s` }} />;
          })}
        </div>
        {/* Cluster halos */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute rounded-full blur-[60px] opacity-40" style={{ background: AOT_CLUSTER_COLOR.ground, width: "38%", height: "62%", left: "5%",  top: "8%" }} />
          <div className="absolute rounded-full blur-[60px] opacity-35" style={{ background: AOT_CLUSTER_COLOR.growth, width: "38%", height: "62%", left: "58%", top: "8%" }} />
          <div className="absolute rounded-full blur-[70px] opacity-25" style={{ background: AOT_CLUSTER_COLOR.strain, width: "26%", height: "38%", left: "38%", top: "44%" }} />
        </div>

        <svg viewBox="0 0 100 50" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full">
          <defs>
            <radialGradient id="aot-node-ground" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={AOT_CLUSTER_COLOR.ground} stopOpacity="0.85" />
              <stop offset="60%" stopColor={AOT_CLUSTER_COLOR.ground} stopOpacity="0.15" />
              <stop offset="100%" stopColor={AOT_CLUSTER_COLOR.ground} stopOpacity="0" />
            </radialGradient>
            <radialGradient id="aot-node-growth" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={AOT_CLUSTER_COLOR.growth} stopOpacity="0.85" />
              <stop offset="60%" stopColor={AOT_CLUSTER_COLOR.growth} stopOpacity="0.15" />
              <stop offset="100%" stopColor={AOT_CLUSTER_COLOR.growth} stopOpacity="0" />
            </radialGradient>
            <radialGradient id="aot-node-strain" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={AOT_CLUSTER_COLOR.strain} stopOpacity="0.85" />
              <stop offset="60%" stopColor={AOT_CLUSTER_COLOR.strain} stopOpacity="0.15" />
              <stop offset="100%" stopColor={AOT_CLUSTER_COLOR.strain} stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Edges (curved beziers) */}
          {AOT_EDGES.map(([a, b, strength], i) => {
            const A = themeById[a]; const B = themeById[b];
            const mx = (A.x + B.x) / 2;
            const my = (A.y + B.y) / 2 - 3;
            const activeE = isEdgeActive([a, b]);
            const w = 0.12 + strength * 0.28;
            const opacity = activeE ? 0.55 + strength * 0.25 : 0.12;
            return (
              <path
                key={i}
                d={`M ${A.x} ${A.y} Q ${mx} ${my} ${B.x} ${B.y}`}
                fill="none"
                stroke={`rgba(238,216,154,${opacity})`}
                strokeWidth={activeE ? w * 1.4 : w}
                strokeLinecap="round"
                className="aot-cn-edge"
              />
            );
          })}

          {/* Nodes */}
          {AOT_THEMES.map((t) => {
            const color = AOT_CLUSTER_COLOR[t.cluster];
            const rGlow = 3.5 + t.count * 0.16;
            const rCore = 0.9 + t.count * 0.08;
            const gradId = `aot-node-${t.cluster}`;
            const dim = active && !isConnected(t.id);
            return (
              <g
                key={t.id}
                className="aot-cn-node"
                style={{ opacity: dim ? 0.25 : 1, transition: "opacity 0.35s ease" }}
                onMouseEnter={() => setActiveAndFetch(t.id)}
                onClick={() => setActiveAndFetch(active === t.id ? null : t.id)}
                data-testid={`aot-cn-${t.id}`}
              >
                {/* Outer soft glow */}
                <circle cx={t.x} cy={t.y} r={rGlow} fill={`url(#${gradId})`} className={active === t.id ? "aot-cn-glow-active" : "aot-cn-glow"} />
                {/* Core star */}
                <circle cx={t.x} cy={t.y} r={rCore} fill={color} />
                <circle cx={t.x} cy={t.y} r={rCore * 0.35} fill="#ffffff" opacity="0.9" />
                {/* Invisible larger hit area */}
                <circle cx={t.x} cy={t.y} r={rGlow + 2} fill="transparent" style={{ cursor: "pointer" }} />
                {/* Label */}
                <text
                  x={t.x} y={t.y + t.labelDy}
                  textAnchor={t.anchor}
                  fontSize="2.6"
                  fontFamily="sans-serif"
                  fill={active === t.id ? "#ffffff" : "rgba(228,232,240,0.92)"}
                  fontWeight={active === t.id ? 600 : 400}
                  style={{ pointerEvents: "none" }}
                >{t.label}</text>
              </g>
            );
          })}
        </svg>

        {/* Excerpt drawer — appears when a theme is active */}
        <div className={`absolute bottom-3 left-3 right-3 rounded-xl border border-white/12 bg-[#0a1530]/85 backdrop-blur px-4 py-3 transition-all duration-300 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"}`} data-testid="aot-cn-excerpt">
          {activeTheme && (() => {
            const isLoading = pending[activeTheme.id];
            const dynamicExcerpt = generated[activeTheme.id];
            const isFailed = Boolean(failed[activeTheme.id]);
            const displayExcerpt = dynamicExcerpt || activeTheme.excerpt;
            const isDynamic = Boolean(dynamicExcerpt);
            return (
              <div className="flex items-start gap-3">
                <span className="mt-1 w-2 h-2 rounded-full flex-shrink-0" style={{ background: AOT_CLUSTER_COLOR[activeTheme.cluster], boxShadow: `0 0 8px ${AOT_CLUSTER_COLOR[activeTheme.cluster]}` }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div className="text-white font-heading text-[13px] leading-tight">{activeTheme.label}</div>
                    <div className="text-[9.5px] uppercase tracking-[0.2em] text-zinc-400">{activeTheme.cluster} · {activeTheme.count} mentions</div>
                    {isDynamic && (
                      <div className="ml-auto inline-flex items-center gap-2">
                        <span className="text-[8.5px] uppercase tracking-[0.24em] text-amber-200/60 inline-flex items-center gap-1"><Sparkles size={9} className="text-amber-300" />AI-composed</span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); regenerate(activeTheme.id); }}
                          className="text-[9px] uppercase tracking-[0.2em] text-teal-200 hover:text-white transition"
                          data-testid="aot-cn-regenerate"
                        >Regenerate ↻</button>
                      </div>
                    )}
                    {isFailed && !isDynamic && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); regenerate(activeTheme.id); }}
                        className="ml-auto text-[9px] uppercase tracking-[0.2em] text-teal-200 hover:text-white transition"
                        data-testid="aot-cn-retry"
                      >Try again ↻</button>
                    )}
                  </div>
                  {isLoading && !dynamicExcerpt ? (
                    <div className="mt-1 space-y-1.5" data-testid="aot-cn-loading">
                      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden"><div className="h-full aot-cn-shimmer" style={{ width: "72%" }} /></div>
                      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden"><div className="h-full aot-cn-shimmer" style={{ width: "44%" }} /></div>
                    </div>
                  ) : (
                    <p className="text-zinc-200 text-[13px] leading-relaxed italic mt-0.5">"{displayExcerpt}"</p>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <p className="text-center mt-3 font-body text-zinc-500 text-[11px] italic">
        Hover any theme to see a sample reflection excerpt — written fresh each time by an AI ghostwriter (Claude). Themes are grouped by whether they steady, expand, or challenge — three natural rhythms of a nurse's inner life.
      </p>
    </div>
  );
}

function AwarenessOverTime() {
  const [range, setRange] = useState("6M");
  const [showModal, setShowModal] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [focusedBiometric, setFocusedBiometric] = useState(null);
  const [syncPulse, setSyncPulse] = useState(0);
  const data = AOT_DATA[range];
  const W = 460, H = 130;
  const aware = buildSmoothPath(data.awareness, W, H);
  const stress = buildSmoothPath(data.stress, W, H);
  const exhaust = buildSmoothPath(data.exhaustion, W, H);
  const focusedSeries = focusedBiometric ? AOT_BIOMETRIC_SERIES[range][focusedBiometric] : null;
  const focusedPath = focusedSeries ? buildSmoothPath(focusedSeries, W, H) : null;
  const focusedMeta = focusedBiometric ? AOT_BIOMETRIC_META[focusedBiometric] : null;
  const toggleFocus = (key) => setFocusedBiometric((v) => (v === key ? null : key));
  // Trigger a "sync pulse" whenever range changes — both phone & dashboard glow briefly in unison
  const changeRange = (r) => { if (r !== range) { setRange(r); setSyncPulse((n) => n + 1); } };
  useEffect(() => {
    if (!syncPulse) return;
    const t = setTimeout(() => {}, 900);
    return () => clearTimeout(t);
  }, [syncPulse]);
  // Lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = showModal ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showModal]);
  // ESC closes modal
  useEffect(() => {
    if (!showModal) return;
    const h = (e) => { if (e.key === "Escape") setShowModal(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [showModal]);
  return (
    <section id="ana-aot-section" className="relative py-24 px-6 sm:px-10" data-testid="ana-aot-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-[42vw] h-[42vw] rounded-full bg-blue-500/12 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[36vw] h-[36vw] rounded-full bg-amber-400/10 blur-[140px]" />
      </div>
      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="reveal max-w-3xl mb-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-teal-300 mb-3">Personal progress insights</div>
          <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl tracking-tight leading-tight text-balance">
            Awareness Over Time.
          </h2>
          <p className="mt-5 font-body text-zinc-300/95 leading-relaxed">
            A private reflective view of the previous three, six, or twelve months. It brings together
            self-reported patterns, meaningful moments, selected reflections, and optional wearable context
            to help the nurse recognize what has changed, what has helped, and what may still need attention.
            This is not a diagnosis, clinical score, or performance report. It is the nurse's own private
            record of change over time.
          </p>
          {/* Awareness Index disclaimer — visible on first view */}
          <div className="mt-5 rounded-xl border border-amber-300/25 bg-white/[0.03] px-4 py-3 flex items-start gap-3" data-testid="ana-awareness-index-disclaimer">
            <Info size={14} className="text-amber-300 mt-0.5 flex-shrink-0" />
            <div className="text-[12.5px] text-zinc-300/90 leading-relaxed">
              <span className="font-heading text-white">What is the Awareness Index?</span>{" "}
              A personal reflective indicator based on voluntary check-ins, recurring themes,
              self-reported changes, selected moments, and optional wearable context. It is designed
              to help the user notice patterns over time.{" "}
              <span className="text-amber-200/90">It is not a validated clinical measurement, diagnosis, or assessment of job performance.</span>
            </div>
          </div>
        </div>

        {/* Editorial diorama — Live Phone + Expanded Dashboard, synced */}
        <div className="reveal aot-diorama relative">
          {/* Ambient stage glow */}
          <div className="aot-stage-glow" aria-hidden />
          {/* Connective light bridge (desktop only) */}
          <div className="aot-light-bridge" aria-hidden />

          <div className="relative grid lg:grid-cols-[minmax(260px,0.78fr)_1.22fr] gap-8 lg:gap-6 items-center">
            {/* LIVE PHONE — mirrors the dashboard state */}
            <div className="relative flex justify-center lg:justify-end lg:pr-2" data-testid="aot-phone-visual">
              <AotLivePhone
                range={range}
                setRange={changeRange}
                hoveredIndex={hoveredIndex}
                setHoveredIndex={setHoveredIndex}
                data={data}
                syncPulse={syncPulse}
                focusedBiometric={focusedBiometric}
                toggleFocus={toggleFocus}
                focusedSeries={focusedSeries}
                focusedMeta={focusedMeta}
              />
            </div>

            {/* Expanded dashboard card — connected preview */}
            <div className={`aot-dash-card relative rounded-3xl border border-amber-300/25 bg-gradient-to-b from-[#0e1d42]/70 to-[#0b1738]/70 backdrop-blur-md shadow-[0_30px_80px_rgba(4,10,30,0.4)] overflow-hidden ${syncPulse ? "aot-sync-flash" : ""}`} key={`dash-${syncPulse}`}>
              {/* Tiny corner label — connection whisper */}
              <div className="absolute top-3 right-4 z-10 inline-flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.28em] text-amber-200/70">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-300 aot-connect-dot" />
                Live from phone
              </div>
          {/* Header + range pills */}
          <div className="px-6 sm:px-8 pt-6 sm:pt-8">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
              <div>
                <div className="text-white font-heading text-2xl sm:text-3xl leading-tight">Awareness Over Time</div>
                <div className="text-zinc-300/80 text-sm mt-1">Your private {range === "3M" ? "3-month" : range === "12M" ? "12-month" : "6-month"} reflective index of emotional patterns, biometrics, and progress.</div>
              </div>
              <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.02] p-1" role="tablist" data-testid="aot-range">
                {["3M","6M","12M"].map((r) => (
                  <button key={r} type="button" onClick={() => changeRange(r)}
                    className={`px-3.5 py-1.5 rounded-full text-[12px] font-heading font-medium tracking-wide transition ${range===r ? "bg-gradient-to-b from-[#F4EAC8] to-[#E6D19E] text-[#0F1E44] shadow-[0_6px_16px_rgba(238,216,154,0.25)]" : "text-zinc-300 hover:text-white"}`}
                    data-testid={`aot-range-${r}`}
                    aria-pressed={range===r}>
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 text-[11px] text-zinc-300/85 mb-3">
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-1 rounded-full bg-[#EED89A]" /> Self-Reported Awareness</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-1 rounded-full bg-[#6ea3ff]" /> Perceived Stress</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-3 h-1 rounded-full bg-[#c5b0ff]" /> Reported Exhaustion</span>
            </div>

            {/* Chart */}
            <div className="relative w-full">
              {focusedBiometric && (
                <div className="absolute -top-2 left-0 z-10 inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#0a1530]/85 backdrop-blur px-3 py-1 text-[10.5px] font-heading tracking-wide" data-testid="aot-focus-badge">
                  <span className="w-2 h-2 rounded-full" style={{ background: focusedMeta.color, boxShadow: `0 0 8px ${focusedMeta.color}` }} />
                  <span className="text-white">Focused · {focusedMeta.label}</span>
                  <span className="text-zinc-400 hidden sm:inline">{focusedMeta.note}</span>
                  <button type="button" onClick={() => setFocusedBiometric(null)} className="text-zinc-300 hover:text-white text-[14px] leading-none ml-1" data-testid="aot-focus-release" aria-label="Release focus">×</button>
                </div>
              )}
              <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto" className="block aot-dash-chart" aria-label="Awareness / Stress / Exhaustion over time">
                {/* grid */}
                {[0,25,50,75,100].map((g) => {
                  const y = 8 + (1 - g/100) * (H - 16);
                  return <line key={g} x1="8" y1={y} x2={W-8} y2={y} stroke="rgba(255,255,255,0.06)" strokeDasharray="2 4" />;
                })}
                {/* exhaustion (behind) */}
                <path d={exhaust.path} fill="none" stroke="#c5b0ff" strokeWidth="2.2" strokeLinecap="round" opacity={focusedBiometric ? 0.18 : 0.85} className="aot-line-base" />
                {/* stress */}
                <path d={stress.path} fill="none" stroke="#6ea3ff" strokeWidth="2.4" strokeLinecap="round" opacity={focusedBiometric ? 0.18 : 0.95} className="aot-line-base" />
                {/* awareness (front) */}
                <path d={aware.path} fill="none" stroke="#EED89A" strokeWidth="2.8" strokeLinecap="round" filter="url(#aot-glow)" opacity={focusedBiometric ? 0.22 : 1} className="aot-line-base" />
                <defs>
                  <filter id="aot-glow" x="-10%" y="-10%" width="120%" height="120%">
                    <feGaussianBlur stdDeviation="1.8" result="b" />
                    <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                  <filter id="aot-focus-glow" x="-15%" y="-15%" width="130%" height="130%">
                    <feGaussianBlur stdDeviation="2.6" result="b" />
                    <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                </defs>
                {/* Focused biometric trend line — drawn on top with reveal animation */}
                {focusedPath && (
                  <g className="aot-focus-line" key={`focus-${focusedBiometric}-${range}`}>
                    <path d={focusedPath.path} fill="none" stroke={focusedMeta.color} strokeWidth="3.2" strokeLinecap="round" filter="url(#aot-focus-glow)" />
                    {focusedPath.points.map(([px, py], i) => (
                      <circle key={i} cx={px} cy={py} r={i === focusedPath.points.length - 1 ? 4.2 : 2.8} fill={focusedMeta.color} stroke="#0e1d42" strokeWidth="1.2" />
                    ))}
                  </g>
                )}
                {/* endpoint dots (base) */}
                {!focusedBiometric && [["#c5b0ff", exhaust.points], ["#6ea3ff", stress.points], ["#EED89A", aware.points]].map(([color, pts], i) => {
                  const [x, y] = pts[pts.length-1];
                  return <circle key={i} cx={x} cy={y} r="3.6" fill={color} stroke="#0e1d42" strokeWidth="1.5" />;
                })}
                {/* Sync scrubber — highlighted vertical line + point when hovered */}
                {hoveredIndex !== null && (() => {
                  const [hx] = aware.points[hoveredIndex];
                  const [, hyA] = aware.points[hoveredIndex];
                  const [, hyS] = stress.points[hoveredIndex];
                  const [, hyE] = exhaust.points[hoveredIndex];
                  return (
                    <g className="aot-scrubber">
                      <line x1={hx} y1={8} x2={hx} y2={H-14} stroke="rgba(238,216,154,0.55)" strokeWidth="1" strokeDasharray="2 3" />
                      {!focusedBiometric && <circle cx={hx} cy={hyE} r="3.2" fill="#c5b0ff" stroke="#fff" strokeWidth="0.8" />}
                      {!focusedBiometric && <circle cx={hx} cy={hyS} r="3.2" fill="#6ea3ff" stroke="#fff" strokeWidth="0.8" />}
                      {!focusedBiometric && <circle cx={hx} cy={hyA} r="4" fill="#EED89A" stroke="#fff" strokeWidth="1" className="aot-scrubber-pulse" />}
                      {focusedPath && (
                        <circle cx={focusedPath.points[hoveredIndex][0]} cy={focusedPath.points[hoveredIndex][1]} r="4.2" fill={focusedMeta.color} stroke="#fff" strokeWidth="1" className="aot-scrubber-pulse" />
                      )}
                    </g>
                  );
                })()}
                {/* Invisible hover hitboxes over each data point */}
                {data.labels.map((_, i) => {
                  const bx = 8 + (i / (data.labels.length - 1)) * (W - 16);
                  const bw = (W - 16) / Math.max(1, data.labels.length - 1);
                  return (
                    <rect
                      key={`hit-${i}`}
                      x={bx - bw / 2}
                      y={0}
                      width={bw}
                      height={H - 14}
                      fill="transparent"
                      style={{ cursor: "crosshair" }}
                      onMouseEnter={() => setHoveredIndex(i)}
                      onMouseLeave={() => setHoveredIndex((v) => (v === i ? null : v))}
                      data-testid={`aot-chart-hit-${i}`}
                    />
                  );
                })}
                {/* x-axis labels */}
                {data.labels.map((lbl, i) => {
                  const x = 8 + (i/(data.labels.length-1)) * (W-16);
                  const active = i === hoveredIndex;
                  return <text key={lbl} x={x} y={H-2} fill={active ? "rgba(238,216,154,0.95)" : "rgba(255,255,255,0.55)"} fontSize="9" textAnchor="middle" fontFamily="var(--font-body)" fontWeight={active ? "600" : "400"}>{lbl}</text>;
                })}
                {/* y-axis labels */}
                {[0,25,50,75,100].map((g) => {
                  const y = 8 + (1 - g/100) * (H - 16) + 3;
                  return <text key={g} x="0" y={y} fill="rgba(255,255,255,0.4)" fontSize="8" fontFamily="var(--font-body)">{g}</text>;
                })}
              </svg>
              {/* Live readout tooltip when hovering */}
              {hoveredIndex !== null && (
                <div className="absolute top-1 right-2 rounded-xl border border-amber-300/30 bg-[#0a1530]/85 backdrop-blur px-3 py-2 text-[11px] shadow-lg pointer-events-none" data-testid="aot-chart-readout">
                  <div className="text-amber-200/85 font-heading tracking-wider uppercase text-[9px] mb-1">{data.labels[hoveredIndex]}</div>
                  {!focusedBiometric && <div className="flex items-center gap-2 text-zinc-100"><span className="w-2 h-2 rounded-full bg-[#EED89A]" /> Self-Reported Awareness · <span className="text-white font-medium">{data.awareness[hoveredIndex]}</span></div>}
                  {!focusedBiometric && <div className="flex items-center gap-2 text-zinc-100"><span className="w-2 h-2 rounded-full bg-[#6ea3ff]" /> Perceived Stress · <span className="text-white font-medium">{data.stress[hoveredIndex]}</span></div>}
                  {!focusedBiometric && <div className="flex items-center gap-2 text-zinc-100"><span className="w-2 h-2 rounded-full bg-[#c5b0ff]" /> Reported Exhaustion · <span className="text-white font-medium">{data.exhaustion[hoveredIndex]}</span></div>}
                  {focusedBiometric && (
                    <div className="flex items-center gap-2 text-zinc-100"><span className="w-2 h-2 rounded-full" style={{ background: focusedMeta.color, boxShadow: `0 0 6px ${focusedMeta.color}` }} /> {focusedMeta.label} · <span className="text-white font-medium">{focusedSeries[hoveredIndex]}</span></div>
                  )}
                </div>
              )}
            </div>

            {/* Emotional intensity band */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-zinc-400 mb-1.5">
                <span>Low</span><span className="text-zinc-300">Emotional Intensity</span><span>High</span>
              </div>
              <div className="h-4 rounded-full overflow-hidden border border-white/10 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%2216%22><defs><linearGradient id=%22g%22 x1=%220%22 y1=%220%22 x2=%221%22 y2=%220%22><stop offset=%220%22 stop-color=%22%232a4470%22/><stop offset=%2235%22 stop-color=%22%23eed89a%22/><stop offset=%2270%22 stop-color=%22%23f6a26a%22/><stop offset=%22100%22 stop-color=%22%23e46a5a%22/></linearGradient></defs><rect width=%22400%22 height=%2216%22 fill=%22url(%23g)%22/></svg>')] bg-cover"></div>
            </div>
          </div>
          <div className="px-6 sm:px-8 pb-6 -mt-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-200/50 flex items-center gap-2">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-300/25 to-transparent" />
              Hover or tap the chart to explore key moments
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-300/25 to-transparent" />
            </div>
          </div>
            </div>
          </div>

          {/* ─── Full-width detail below the diorama ─── */}
          <div className="mt-6 rounded-3xl border border-amber-300/25 bg-gradient-to-b from-[#0e1d42]/70 to-[#0b1738]/70 backdrop-blur-md shadow-[0_30px_80px_rgba(4,10,30,0.4)] overflow-hidden pt-2">

          {/* Optional Wearable Context */}
          <div className="px-6 sm:px-8 mt-8">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-[0.24em] text-teal-300">Optional Wearable Context</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-amber-200/60">Tap a tile to isolate on chart ↑</div>
            </div>
            <p className="text-[11.5px] text-zinc-500 italic leading-relaxed mb-3">
              Illustrative display. Available measurements, terminology, and calculations depend on the connected device and the user's permissions.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { key: "sleep",    icon: Moon,       name: "Sleep",    value: "7.2h",  trend: "+12%", up: true },
                { key: "hrv",      icon: HeartPulse, name: "HRV",      value: "62 ms", trend: "+8%",  up: true },
                { key: "recovery", icon: TrendingUp, name: "Recovery", value: "78%",   trend: "+15%", up: true },
                { key: "stress",   icon: Activity,   name: "Stress indicator",   value: "28",    trend: "-18%", up: true },
              ].map(({ key, icon: Icon, name, value, trend, up }) => {
                const active = focusedBiometric === key;
                const color = AOT_BIOMETRIC_META[key].color;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => toggleFocus(key)}
                    className={`text-left rounded-2xl border p-4 transition-all duration-300 ${active ? "aot-tile-active" : "border-white/10 bg-white/[0.03] hover:border-amber-300/40 hover:bg-white/[0.05]"}`}
                    style={active ? { borderColor: color, boxShadow: `0 0 0 1px ${color}55, 0 8px 24px ${color}22`, background: `linear-gradient(180deg, ${color}18, ${color}08)` } : undefined}
                    aria-pressed={active}
                    data-testid={`aot-biometric-tile-${key}`}
                  >
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em]" style={{ color: active ? color : undefined }}>
                      <Icon size={12} className={active ? "" : "text-[#EED89A]"} style={active ? { color } : undefined} /> {name}
                    </div>
                    <div className="mt-1.5 text-white font-heading font-medium text-2xl leading-none">{value}</div>
                    <div className={`mt-1 text-[11px] ${up ? "text-emerald-300/90" : "text-rose-300/90"}`}>{trend}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Key moments */}
          <div className="px-6 sm:px-8 mt-8">
            <div className="text-[11px] uppercase tracking-[0.24em] text-teal-300 mb-3">Key moments</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {AOT_KEY_MOMENTS.map((m) => (
                <div key={m.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-start gap-2 mb-1">
                    <span className="text-[16px]" aria-hidden>{m.icon}</span>
                    <div>
                      <div className="text-white font-heading text-[14.5px] leading-snug">{m.title}</div>
                      <div className="text-zinc-400 text-[11px] mt-0.5">{m.date}</div>
                    </div>
                  </div>
                  <p className="text-zinc-300/85 text-[13px] leading-relaxed">{m.body}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Conversations creating awareness — constellation */}
          <div className="px-6 sm:px-8 mt-8">
            <ThemeConstellation />
          </div>

          {/* Anchor + Compass — animated diptych */}
          <div className="px-6 sm:px-8 mt-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* ⚓ ANCHOR */}
              <div className="aot-anchor-card relative rounded-2xl border border-blue-300/25 bg-gradient-to-br from-[#0d2350]/80 to-[#0a1a3a]/80 p-6 overflow-hidden" data-testid="aot-anchor">
                <div className="anchor-ripple r1" aria-hidden />
                <div className="anchor-ripple r2" aria-hidden />
                <div className="anchor-ripple r3" aria-hidden />
                <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="anchor-icon-wrap"><AnchorIcon size={18} className="text-sky-200" /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-sky-200/85">Your Anchor</div>
                    <div className="font-heading text-white text-lg leading-tight">A gentle synopsis, not a diagnosis</div>
                  </div>
                </div>
                <div className="relative z-10 space-y-3 anchor-text">
                  <p className="font-body text-zinc-100/90 text-[14.5px] leading-relaxed anchor-line" style={{ animationDelay: "0.4s" }}>
                    Over these six months, your awareness has strengthened as sleep and evening rhythms steadied.
                  </p>
                  <p className="font-body text-zinc-100/90 text-[14.5px] leading-relaxed anchor-line" style={{ animationDelay: "1.4s" }}>
                    Stress rose sharply around appointment weeks, and each time your recovery came back a little faster.
                  </p>
                  <p className="font-body text-zinc-100/90 text-[14.5px] leading-relaxed anchor-line" style={{ animationDelay: "2.4s" }}>
                    Boundary-setting and rest were the moments that shifted the most.
                  </p>
                </div>
                <div className="relative z-10 mt-5 text-[10px] uppercase tracking-[0.24em] text-sky-200/60">Anchor holds you in what has been</div>
              </div>

              {/* 🧭 COMPASS */}
              <div className="aot-compass-card relative rounded-2xl border border-amber-300/30 bg-gradient-to-br from-[#22183a]/70 to-[#1a1330]/80 p-6 overflow-hidden" data-testid="aot-compass">
                <svg className="compass-rose" viewBox="0 0 200 200" aria-hidden>
                  <defs>
                    <radialGradient id="cr-glow" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="rgba(238,216,154,0.35)" />
                      <stop offset="70%" stopColor="rgba(238,216,154,0)" />
                    </radialGradient>
                  </defs>
                  <circle cx="100" cy="100" r="98" fill="url(#cr-glow)" />
                  <circle cx="100" cy="100" r="70" fill="none" stroke="rgba(238,216,154,0.28)" strokeWidth="0.6" />
                  <circle cx="100" cy="100" r="52" fill="none" stroke="rgba(238,216,154,0.20)" strokeWidth="0.4" strokeDasharray="1 3" />
                  {/* Tick marks */}
                  {Array.from({ length: 24 }).map((_, i) => {
                    const angle = (i * 15) * Math.PI / 180;
                    const inner = i % 3 === 0 ? 62 : 66;
                    const outer = 70;
                    const x1 = 100 + Math.cos(angle) * inner;
                    const y1 = 100 + Math.sin(angle) * inner;
                    const x2 = 100 + Math.cos(angle) * outer;
                    const y2 = 100 + Math.sin(angle) * outer;
                    return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(238,216,154,0.4)" strokeWidth={i % 6 === 0 ? 1 : 0.5} />;
                  })}
                  {/* Cardinal letters */}
                  {[["N", 100, 40],["E", 158, 104],["S", 100, 168],["W", 42, 104]].map(([l, x, y]) => (
                    <text key={l} x={x} y={y} fill="rgba(238,216,154,0.85)" fontSize="9" fontFamily="var(--font-heading)" textAnchor="middle" dominantBaseline="middle">{l}</text>
                  ))}
                  {/* Rotating needle */}
                  <g className="compass-needle">
                    <polygon points="100,42 106,102 100,110 94,102" fill="#EED89A" opacity="0.95" />
                    <polygon points="100,158 106,98 100,90 94,98" fill="rgba(255,255,255,0.25)" />
                    <circle cx="100" cy="100" r="3" fill="#EED89A" />
                  </g>
                </svg>

                <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="compass-icon-wrap"><Compass size={18} className="text-amber-200" /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-amber-200/85">Your Compass</div>
                    <div className="font-heading text-white text-lg leading-tight">Three gentle directions</div>
                  </div>
                </div>

                <div className="relative z-10 space-y-2.5">
                  {[
                    { k: "Notice", body: "the pattern before appointment weeks — a small preparation ritual may help." },
                    { k: "Try", body: "one evening reset the night before a demanding conversation." },
                    { k: "Return", body: "to a moment of self-trust when overwhelm begins to rise." },
                  ].map(({ k, body }, i) => (
                    <div key={k} className="compass-step" style={{ animationDelay: `${1.2 + i * 0.5}s` }}>
                      <div className="flex items-baseline gap-2.5">
                        <span className="text-[#EED89A] font-heading font-medium text-[13px] tracking-wide uppercase min-w-[52px]">{k}</span>
                        <span className="text-zinc-100/90 text-[13.5px] leading-relaxed">{body}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="relative z-10 mt-5 text-[10px] uppercase tracking-[0.24em] text-amber-200/60">Compass points toward what could be next</div>
              </div>
            </div>
            <p className="text-center mt-4 font-body text-zinc-500 text-[11.5px] italic">
              Anchor holds you in what has been. Compass points you gently toward what could be next. Neither replaces a clinician.
            </p>
          </div>

          {/* Footer / CTA */}
          <div className="px-6 sm:px-8 pt-7 pb-8 mt-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-300/80">
                <Lock size={12} className="text-[#EED89A]" /> Private. Personal. Protected.
              </div>
              <div className="inline-flex items-center gap-3">
                <button type="button" onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-gradient-to-b from-[#F4EAC8] to-[#E6D19E] text-[#0F1E44] font-heading font-medium text-sm shadow-[0_10px_24px_rgba(238,216,154,0.28)] hover:shadow-[0_10px_28px_rgba(238,216,154,0.4)] transition" data-testid="aot-cta-primary">
                  Explore Key Moments <ArrowRight size={14} />
                </button>
                <button type="button" onClick={() => setShowModal(true)} className="text-teal-200 hover:text-white text-[13px] tracking-tight inline-flex items-center gap-1 underline decoration-transparent hover:decoration-teal-200 underline-offset-4 transition" data-testid="aot-cta-secondary">
                  Open my full reflection page <ArrowUpRight size={12} />
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>

        <p className="reveal mt-6 max-w-2xl font-body text-zinc-400 italic text-[13.5px] leading-relaxed">
          Not a diagnosis. Not a performance report. A nurse's own private record of what she has been
          moving through, what helped, and how her awareness has evolved.
        </p>
      </div>

      {showModal && <FullReflectionModal onClose={() => setShowModal(false)} range={range} data={data} />}
    </section>
  );
}

function FullReflectionModal({ onClose, range, data }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-testid="aot-modal" role="dialog" aria-modal="true" aria-label="Full Reflection Page preview">
      <div className="absolute inset-0 bg-[#04081a]/85 backdrop-blur-xl" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-6xl max-h-[92vh] overflow-y-auto rounded-3xl border border-amber-300/30 bg-gradient-to-b from-[#0e1d42] to-[#0a1530] shadow-[0_50px_120px_rgba(0,0,0,0.7)] modal-in">
        {/* Sticky close bar — very obvious */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 sm:px-8 py-4 bg-gradient-to-b from-[#0e1d42] to-[#0e1d42]/85 backdrop-blur border-b border-amber-300/20">
          <div className="flex items-center gap-3">
            <Sparkles size={16} className="text-amber-300" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300/80">Preview · concept only</div>
              <div className="text-white font-heading text-lg">My Full Reflection Page</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-white/[0.08] hover:bg-white/[0.14] border border-white/20 hover:border-amber-300/60 text-white font-heading font-medium text-sm transition"
            data-testid="aot-modal-close"
            autoFocus
          >
            Close <span aria-hidden className="text-lg leading-none -mt-0.5">×</span>
          </button>
        </div>

        <div className="px-6 sm:px-10 py-8 space-y-8">
          {/* Intro */}
          <div>
            <h3 className="font-heading font-medium text-white text-3xl leading-tight">This is your personal reflection space.</h3>
            <p className="mt-3 text-zinc-300 max-w-2xl">See how your conversations, biometrics, and moments of awareness have connected over time. This preview shows the {range === "3M" ? "3-month" : range === "12M" ? "12-month" : "6-month"} view.</p>
          </div>

          {/* Pattern recognition — AI summary card */}
          <div className="rounded-2xl border border-amber-300/25 bg-white/[0.03] p-6">
            <div className="text-[10px] uppercase tracking-[0.24em] text-amber-300 mb-2">Pattern recognition</div>
            <p className="text-zinc-100 text-[15px] leading-relaxed">
              Over the last {range === "3M" ? "twelve weeks" : range === "12M" ? "twelve months" : "six months"}, moments of overwhelm often appeared before appointments and after emotionally demanding conversations. Awareness increased most after reflective sessions about boundaries, rest, and self-trust. Sleep and recovery improved during periods of more consistent evening use.
            </p>
          </div>

          {/* Monthly timeline strip */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-teal-300 mb-3">Monthly timeline</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {data.labels.map((m, i) => (
                <div key={m} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-amber-300 font-heading text-lg leading-none">{m}</div>
                  <div className="mt-2 text-[11px] uppercase tracking-wide text-zinc-400">Awareness</div>
                  <div className="text-white font-heading">{data.awareness[i]}</div>
                  <div className="mt-1 h-1 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#EED89A] to-[#F4EAC8]" style={{ width: `${data.awareness[i]}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Personal progress reflection */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="text-[10px] uppercase tracking-[0.24em] text-teal-300 mb-2">What has improved</div>
              <p className="text-zinc-100 text-[14px] leading-relaxed">Evening rest, boundary-setting confidence, recovery time after difficult conversations.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="text-[10px] uppercase tracking-[0.24em] text-teal-300 mb-2">What still needs support</div>
              <p className="text-zinc-100 text-[14px] leading-relaxed">Pre-appointment anxiety spikes and post-shift emotional decompression.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="text-[10px] uppercase tracking-[0.24em] text-teal-300 mb-2">Conversations that helped most</div>
              <p className="text-zinc-100 text-[14px] leading-relaxed">Self-trust, letting go, and gratitude sessions had the strongest downstream effect.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="text-[10px] uppercase tracking-[0.24em] text-teal-300 mb-2">Where resilience is forming</div>
              <p className="text-zinc-100 text-[14px] leading-relaxed">Stress rose, but returned to baseline faster with each cycle — a signal of adaptive capacity.</p>
            </div>
          </div>

          {/* Session archive teaser */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-teal-300 mb-3">Session archive · filters</div>
            <div className="flex flex-wrap gap-2">
              {["Most calming", "Most clarifying", "Most emotional", "Most restorative", "Related to sleep", "Related to relationships"].map((f) => (
                <span key={f} className="text-[12px] px-3 py-1.5 rounded-full border border-white/12 bg-white/[0.03] text-zinc-200">{f}</span>
              ))}
            </div>
          </div>

          <div className="pt-2 pb-2 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[11px] uppercase tracking-[0.24em] text-zinc-400"><Lock size={11} className="inline mb-0.5 mr-1 text-amber-300" /> Private. Personal. Protected.</p>
            <button type="button" onClick={onClose} className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-gradient-to-b from-[#F4EAC8] to-[#E6D19E] text-[#0F1E44] font-heading font-medium text-sm shadow-[0_10px_24px_rgba(238,216,154,0.28)]" data-testid="aot-modal-close-bottom">
              Close preview <span aria-hidden className="text-base leading-none">×</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AnaPage() {
  const containerRef = useReveal();
  const [docsOpen, setDocsOpen] = useState(false);
  const [jointOpen, setJointOpen] = useState(false);
  const [activeId, setActiveId] = useState("overview");

  useEffect(() => {
    const ids = ["overview", "nurse-experience", "ana-integration", "privacy-doctrine", "behavioral-doctrine", "ana-aot-section", "pilot-proposal"];
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!("IntersectionObserver" in window) || !els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          // Behavioral doctrine still lights up the "Privacy & Doctrine" tab
          const raw = visible[0].target.id;
          setActiveId(raw === "behavioral-doctrine" ? "privacy-doctrine" : raw);
        }
      },
      { rootMargin: "-25% 0px -65% 0px", threshold: 0 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  // Randomized, soft 3D sparkle stars — stable across renders
  const stars = useMemo(() => {
    const rand = (min, max) => min + Math.random() * (max - min);
    return Array.from({ length: 26 }).map((_, i) => ({
      left: rand(2, 97),
      top: rand(3, 95),
      size: rand(5, 13),
      dur: rand(6.5, 13),
      delay: rand(0, 8),
      rot: rand(-35, 35),
      spin: rand(30, 80) * (i % 2 ? 1 : -1),
      driftX: rand(-9, 9),
      driftY: rand(-7, 7),
      gold: i % 2 === 0,
      depth: rand(0.5, 0.9),
    }));
  }, []);
  if (sessionStorage.getItem("mmg_ana_access") !== "granted") return <Navigate to="/ana-access" replace />;

  return (
    <div ref={containerRef} className="relative min-h-screen w-full ana-scope" style={{ overflowX: "clip" }} data-testid="ana-page">
      <div className="ana-sparkle-b" aria-hidden="true" />
      <div className="ana-aurora" aria-hidden="true" />
      <div className="ana-motes" aria-hidden="true">
        {Array.from({ length: 18 }).map((_, i) => {
          const gold = i % 3 === 0;
          const large = i % 5 === 0;
          const left = (i * 37) % 100;
          const delay = ((i * 11) % 90) / 10;
          const dur = 14 + ((i * 7) % 16);
          return (
            <span
              key={i}
              className={`ana-mote${gold ? " gold" : ""}${large ? " large" : ""}`}
              style={{ left: `${left}%`, animationDelay: `${delay}s`, animationDuration: `${dur}s` }}
            />
          );
        })}
      </div>
      <div className="ana-shoot" aria-hidden="true" />
      <div className="ana-shoot two" aria-hidden="true" />
      {/* Twinkling stars — soft, 3D, randomized */}
      {stars.map((s, i) => (
        <span
          key={`s${i}`}
          className={`ana-star ${s.gold ? "gold" : ""}`}
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            "--size": `${s.size}px`,
            "--dur": `${s.dur}s`,
            "--delay": `${s.delay}s`,
            "--rot": `${s.rot}deg`,
            "--spin": `${s.spin}deg`,
            "--dx": `${s.driftX}px`,
            "--dy": `${s.driftY}px`,
            "--depth": s.depth,
          }}
          aria-hidden="true"
        >
          <span className="core" />
        </span>
      ))}
      <style>{`
        html { scroll-behavior: smooth; }
        section[id] { scroll-margin-top: 88px; }
        .ana-scope{
          background:
            radial-gradient(1200px 700px at 15% -10%, rgba(96,165,250,0.32), transparent 60%),
            radial-gradient(900px 700px at 90% 10%, rgba(59,130,246,0.24), transparent 60%),
            radial-gradient(1000px 900px at 50% 110%, rgba(37,99,235,0.28), transparent 60%),
            linear-gradient(180deg, #0d1b3d 0%, #14285a 35%, #1a3572 60%, #10254f 85%, #0a1836 100%);
        }
        .ana-scope::before{
          content:"";position:absolute;inset:0;pointer-events:none;z-index:0;
          background-image:
            radial-gradient(1.6px 1.6px at 12% 18%, rgba(251,191,36,0.95), transparent 60%),
            radial-gradient(1.2px 1.2px at 68% 30%, rgba(253,224,71,0.85), transparent 60%),
            radial-gradient(2px 2px at 40% 12%, rgba(252,211,77,1), transparent 55%),
            radial-gradient(1.4px 1.4px at 22% 72%, rgba(253,224,71,0.9), transparent 60%),
            radial-gradient(1.2px 1.2px at 92% 22%, rgba(252,211,77,0.9), transparent 60%),
            radial-gradient(1.6px 1.6px at 55% 62%, rgba(251,191,36,0.95), transparent 60%),
            radial-gradient(1.4px 1.4px at 33% 88%, rgba(252,211,77,0.85), transparent 60%),
            radial-gradient(1.8px 1.8px at 78% 78%, rgba(251,191,36,0.9), transparent 60%),
            radial-gradient(1.4px 1.4px at 6% 32%, rgba(255,255,255,0.95), transparent 55%),
            radial-gradient(1.6px 1.6px at 28% 50%, rgba(255,255,255,0.9), transparent 55%),
            radial-gradient(1.2px 1.2px at 52% 26%, rgba(255,255,255,0.85), transparent 55%),
            radial-gradient(1.5px 1.5px at 74% 44%, rgba(255,255,255,0.9), transparent 55%),
            radial-gradient(1.2px 1.2px at 88% 66%, rgba(255,255,255,0.85), transparent 55%),
            radial-gradient(1.4px 1.4px at 44% 82%, rgba(255,255,255,0.9), transparent 55%),
            radial-gradient(1.2px 1.2px at 16% 92%, rgba(255,255,255,0.85), transparent 55%),
            radial-gradient(1.5px 1.5px at 66% 96%, rgba(255,255,255,0.85), transparent 55%),
            radial-gradient(1.2px 1.2px at 60% 8%, rgba(255,255,255,0.9), transparent 55%);
          animation: anaSparkleA 4.5s ease-in-out infinite alternate;
        }
        .ana-scope > .ana-sparkle-b{
          position:absolute;inset:0;pointer-events:none;z-index:0;
          background-image:
            radial-gradient(1.3px 1.3px at 82% 14%, rgba(251,191,36,0.85), transparent 60%),
            radial-gradient(1.5px 1.5px at 38% 60%, rgba(253,224,71,0.9), transparent 60%),
            radial-gradient(1.2px 1.2px at 4% 68%, rgba(252,211,77,0.85), transparent 60%),
            radial-gradient(1.4px 1.4px at 96% 50%, rgba(251,191,36,0.85), transparent 60%),
            radial-gradient(1.1px 1.1px at 50% 44%, rgba(253,224,71,0.8), transparent 60%),
            radial-gradient(1.3px 1.3px at 30% 20%, rgba(255,255,255,0.9), transparent 55%),
            radial-gradient(1.1px 1.1px at 82% 82%, rgba(255,255,255,0.85), transparent 55%),
            radial-gradient(1.4px 1.4px at 20% 40%, rgba(255,255,255,0.9), transparent 55%),
            radial-gradient(1.2px 1.2px at 62% 72%, rgba(255,255,255,0.85), transparent 55%),
            radial-gradient(1.1px 1.1px at 8% 12%, rgba(255,255,255,0.85), transparent 55%);
          animation: anaSparkleB 6s ease-in-out infinite alternate;
        }
        @keyframes anaSparkleA{ from{opacity:.45} to{opacity:1} }
        @keyframes anaSparkleB{ from{opacity:1} to{opacity:.4} }
        .ana-scope > .ana-aurora{position:absolute;inset:-5% -10% 40% -10%;pointer-events:none;z-index:0;mix-blend-mode:screen;filter:blur(60px);opacity:.55;
          background:
            radial-gradient(45% 30% at 20% 30%, rgba(96,165,250,0.55), transparent 60%),
            radial-gradient(35% 25% at 78% 22%, rgba(251,191,36,0.30), transparent 60%),
            radial-gradient(40% 30% at 55% 65%, rgba(56,189,248,0.45), transparent 60%);
          animation: anaAuroraShift 22s ease-in-out infinite alternate;
        }
        @keyframes anaAuroraShift{
          0%{transform:translate3d(-3%,-1%,0) scale(1.02)}
          50%{transform:translate3d(4%,2%,0) scale(1.08)}
          100%{transform:translate3d(-2%,1%,0) scale(1.04)}
        }
        .ana-scope > .ana-motes{position:absolute;inset:0;pointer-events:none;z-index:0;overflow:hidden}
        .ana-mote{position:absolute;width:5px;height:5px;border-radius:50%;background:#fff;
          box-shadow:0 0 14px 3px rgba(255,255,255,1), 0 0 32px 8px rgba(147,197,253,0.7), 0 0 60px 14px rgba(96,165,250,0.35);
          animation: anaMoteRise linear infinite;
        }
        .ana-mote.gold{background:#fde68a;box-shadow:0 0 14px 3px rgba(253,224,71,1), 0 0 34px 10px rgba(251,191,36,0.75), 0 0 60px 16px rgba(245,158,11,0.35)}
        .ana-mote.large{width:7px;height:7px;box-shadow:0 0 18px 5px rgba(255,255,255,1), 0 0 40px 12px rgba(147,197,253,0.7), 0 0 80px 22px rgba(96,165,250,0.35)}
        .priv-flow-arrow{ animation: privArrow 2.4s ease-in-out infinite; }
        @keyframes privArrow{ 0%,100%{ transform: translateY(-2px); opacity:.7; } 50%{ transform: translateY(2px); opacity:1; } }
        @media (prefers-reduced-motion: reduce){ .priv-flow-arrow{ animation:none; } }
        .mmg-wear{ filter: drop-shadow(0 20px 40px rgba(4,12,40,0.5)); }
        .mmg-wear-a{ animation: mmgWearA 8s ease-in-out infinite; }
        .mmg-wear-b{ animation: mmgWearB 9.5s ease-in-out infinite; }
        @keyframes mmgWearA{ 0%,100%{ transform: translateY(0) rotate(0deg);} 50%{ transform: translateY(-10px) rotate(-1deg);} }
        @keyframes mmgWearB{ 0%,100%{ transform: translateY(0) rotate(0deg);} 50%{ transform: translateY(8px) rotate(1.5deg);} }
        @media (prefers-reduced-motion: reduce){ .mmg-wear-a, .mmg-wear-b{ animation:none; } }
        .ana-window-pulse{ position:relative; overflow:hidden; animation: anaWindowPulse 3.6s ease-in-out infinite; }
        .ana-window-pulse::after{ content:""; position:absolute; inset:0; pointer-events:none;
          background:linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.14) 50%, transparent 100%);
          transform:translateX(-120%); animation: anaWindowSweep 4.2s ease-in-out infinite; }
        @keyframes anaWindowPulse{
          0%,100%{ box-shadow: 0 0 18px rgba(45,212,191,0.12); border-color: rgba(94,234,212,0.4); }
          50%{ box-shadow: 0 0 34px rgba(45,212,191,0.32); border-color: rgba(94,234,212,0.75); }
        }
        @keyframes anaWindowSweep{ 0%{ transform:translateX(-120%);} 55%,100%{ transform:translateX(120%);} }
        @media (prefers-reduced-motion: reduce){ .ana-window-pulse, .ana-window-pulse::after{ animation:none; } }
        .ana-star{position:absolute;width:var(--size);height:var(--size);pointer-events:none;z-index:0;
          transform-style:preserve-3d;
          --c1:rgba(255,255,255,1);
          --c2:rgba(191,219,254,0.95);
          --c3:rgba(96,165,250,0.55);
          animation: anaTwinkle var(--dur) ease-in-out infinite;
          animation-delay: var(--delay);
          will-change: transform, opacity;
        }
        .ana-star.gold{
          --c1:rgba(255,251,235,1);
          --c2:rgba(253,224,71,0.98);
          --c3:rgba(245,158,11,0.55);
        }
        .ana-star::before,.ana-star::after{content:"";position:absolute;top:50%;left:50%;
          transform:translate(-50%,-50%);border-radius:50%;
          background:radial-gradient(closest-side, var(--c1), var(--c2) 32%, transparent 72%);
          filter:blur(0.5px);
        }
        .ana-star::before{width:13%;height:155%}
        .ana-star::after{width:155%;height:13%}
        .ana-star .core{position:absolute;top:50%;left:50%;width:48%;height:48%;
          transform:translate(-50%,-50%);border-radius:50%;
          background:radial-gradient(circle at 38% 34%, #fff 0%, var(--c1) 20%, var(--c2) 54%, transparent 80%);
          box-shadow:0 0 6px 1px var(--c2), 0 0 16px 4px var(--c3), 0 0 30px 8px var(--c3);
          filter:blur(0.4px);
        }
        @keyframes anaTwinkle{
          0%   {opacity:.06; transform:scale(calc(.35*var(--depth))) rotate(var(--rot)) rotate3d(1,1,0,0deg) translate(0,0)}
          28%  {opacity:.5}
          50%  {opacity:.72; transform:scale(calc(1.0*var(--depth))) rotate(calc(var(--rot) + var(--spin))) rotate3d(1,1,0,22deg) translate(var(--dx),var(--dy))}
          74%  {opacity:.4}
          100% {opacity:.06; transform:scale(calc(.35*var(--depth))) rotate(calc(var(--rot) + var(--spin) * 2)) rotate3d(1,1,0,0deg) translate(0,0)}
        }
        @keyframes anaMoteRise{
          0%{transform:translate3d(0,20vh,0) scale(.6);opacity:0}
          10%{opacity:1}
          85%{opacity:1}
          100%{transform:translate3d(20px,-110vh,0) scale(1.1);opacity:0}
        }
        .ana-shoot{position:absolute;top:12%;left:8%;width:2px;height:2px;border-radius:9999px;background:#fff;
          box-shadow:0 0 12px 3px rgba(255,255,255,0.95), 0 0 26px 8px rgba(191,219,254,0.6);
          animation: anaShoot 8s cubic-bezier(0.22,1,0.36,1) infinite;
        }
        .ana-shoot::after{content:"";position:absolute;top:50%;right:100%;transform:translateY(-50%);
          width:120px;height:2px;border-radius:9999px;
          background:linear-gradient(90deg,transparent,rgba(255,255,255,0.9),rgba(191,219,254,1));
          box-shadow:0 0 10px 2px rgba(255,255,255,0.6);
        }
        .ana-shoot.two{top:36%;left:auto;right:14%;animation-delay:4s;animation-duration:9s}
        .ana-shoot.two::after{background:linear-gradient(90deg,transparent,rgba(252,211,77,0.9),rgba(251,191,36,1))}
        @keyframes anaShoot{
          0%,88%{transform:translate3d(0,0,0) rotate(20deg);opacity:0}
          89%{opacity:1}
          98%{transform:translate3d(340px,120px,0) rotate(20deg);opacity:.8}
          100%{transform:translate3d(360px,130px,0) rotate(20deg);opacity:0}
        }
        .ana-scope .glass{ background: rgba(255,255,255,0.028); border-color: rgba(45,212,191,0.16); box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 0 34px rgba(45,212,191,0.06) }

        /* ANCHOR — drops in, ripples emanate outward, text writes itself line by line */
        .aot-anchor-card { animation: anchorSettle 1.4s cubic-bezier(0.2,0.9,0.3,1) both; }
        @keyframes anchorSettle {
          0% { opacity: 0; transform: translateY(-20px); }
          60% { opacity: 1; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .anchor-icon-wrap {
          width: 38px; height: 38px; border-radius: 50%;
          background: rgba(120,170,240,0.12); border: 1px solid rgba(140,190,255,0.35);
          display: flex; align-items: center; justify-content: center;
          animation: anchorBob 6s ease-in-out infinite;
          box-shadow: 0 0 18px rgba(140,190,255,0.15);
        }
        @keyframes anchorBob { 0%,100% { transform: translateY(0) rotate(-2deg); } 50% { transform: translateY(-3px) rotate(2deg); } }

        .anchor-ripple {
          position: absolute; left: 34px; top: 34px; width: 20px; height: 20px;
          border-radius: 50%; border: 1px solid rgba(140,190,255,0.4);
          transform: translate(-50%, -50%);
          animation: anchorRipple 4.4s ease-out infinite;
          pointer-events: none;
        }
        .anchor-ripple.r1 { animation-delay: 0s; }
        .anchor-ripple.r2 { animation-delay: 1.4s; }
        .anchor-ripple.r3 { animation-delay: 2.8s; }
        @keyframes anchorRipple {
          0%   { width: 20px; height: 20px; opacity: 0.7; border-width: 1.2px; }
          80%  { width: 260px; height: 260px; opacity: 0; border-width: 0.4px; }
          100% { width: 260px; height: 260px; opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) { .anchor-ripple, .anchor-icon-wrap, .aot-anchor-card { animation: none; } }

        .anchor-line {
          opacity: 0;
          animation: anchorLineIn 0.9s cubic-bezier(0.2,0.9,0.3,1) forwards;
        }
        @keyframes anchorLineIn {
          0% { opacity: 0; transform: translateY(6px); filter: blur(3px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        @media (prefers-reduced-motion: reduce) { .anchor-line { opacity: 1; animation: none; transform: none; filter: none; } }

        /* COMPASS — rose glows, needle spins then settles, steps reveal in sequence */
        .aot-compass-card { animation: compassSettle 1.4s cubic-bezier(0.2,0.9,0.3,1) both; animation-delay: 0.3s; }
        @keyframes compassSettle {
          0% { opacity: 0; transform: translateY(-18px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        .compass-icon-wrap {
          width: 38px; height: 38px; border-radius: 50%;
          background: rgba(238,216,154,0.14); border: 1px solid rgba(238,216,154,0.45);
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 0 18px rgba(238,216,154,0.2);
        }

        .compass-rose {
          position: absolute; right: -30px; bottom: -30px; width: 260px; height: 260px;
          opacity: 0.28;
          animation: roseFloat 12s ease-in-out infinite;
          pointer-events: none; z-index: 0;
        }
        @keyframes roseFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }

        .compass-needle {
          transform-origin: 100px 100px;
          animation: needleCalibrate 6s cubic-bezier(0.2,0.9,0.3,1) 0.3s infinite;
        }
        @keyframes needleCalibrate {
          0%   { transform: rotate(0deg); }
          10%  { transform: rotate(320deg); }
          22%  { transform: rotate(140deg); }
          32%  { transform: rotate(220deg); }
          42%  { transform: rotate(190deg); }
          50%  { transform: rotate(200deg); }
          100% { transform: rotate(200deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .compass-needle { animation: none; transform: rotate(200deg); }
          .compass-rose { animation: none; }
          .aot-compass-card { animation: none; }
        }

        .compass-step {
          opacity: 0;
          animation: compassStepIn 0.7s cubic-bezier(0.2,0.9,0.3,1) forwards;
        }
        @keyframes compassStepIn {
          0% { opacity: 0; transform: translateX(-8px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @media (prefers-reduced-motion: reduce) { .compass-step { opacity: 1; animation: none; transform: none; } }

        /* AOT phone visual float */
        .aot-phone-float { animation: aotPhoneFloat 9s ease-in-out infinite; filter: drop-shadow(0 40px 80px rgba(4,10,30,0.6)); }
        @keyframes aotPhoneFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @media (prefers-reduced-motion: reduce) { .aot-phone-float { animation: none; } }

        /* ────── AOT Editorial Diorama — Live Phone + Dashboard ────── */
        .aot-diorama { padding: 8px 0 12px; }
        .aot-stage-glow {
          position: absolute; inset: -40px -20px -60px -20px;
          background:
            radial-gradient(60% 45% at 22% 55%, rgba(238,216,154,0.14), transparent 70%),
            radial-gradient(55% 45% at 78% 50%, rgba(96,151,255,0.12), transparent 70%);
          filter: blur(10px);
          pointer-events: none; z-index: 0;
        }
        .aot-light-bridge {
          display: none;
          position: absolute; z-index: 1;
          top: 32%; left: 30%; width: 42%; height: 20%;
          background: radial-gradient(ellipse at center, rgba(238,216,154,0.18), rgba(238,216,154,0) 70%);
          filter: blur(30px);
          pointer-events: none;
          animation: bridgeShimmer 6s ease-in-out infinite;
        }
        @media (min-width: 1024px) { .aot-light-bridge { display: block; } }
        @keyframes bridgeShimmer { 0%,100% { opacity: 0.7; } 50% { opacity: 1; } }

        /* Live iPhone frame */
        .aot-phone-wrap {
          position: relative;
          width: 100%;
          max-width: 280px;
          animation: aotPhoneBreath 9s ease-in-out infinite;
          filter: drop-shadow(0 30px 60px rgba(4,10,30,0.7));
        }
        @keyframes aotPhoneBreath {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-8px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .aot-phone-wrap { animation: none; }
        }
        .aot-phone-halo {
          position: absolute; inset: -30px -30px -60px -30px;
          background: radial-gradient(60% 55% at 50% 55%, rgba(238,216,154,0.28), rgba(96,151,255,0.10) 55%, transparent 75%);
          filter: blur(40px);
          z-index: -1;
          pointer-events: none;
          animation: haloPulse 7s ease-in-out infinite;
        }
        @keyframes haloPulse { 0%,100% { opacity: 0.85; } 50% { opacity: 1; } }

        .aot-phone-frame {
          position: relative;
          border-radius: 44px;
          padding: 6px;
          background:
            linear-gradient(160deg, #2c2c34 0%, #131318 40%, #0a0a10 100%);
          box-shadow:
            0 0 0 1px rgba(255,255,255,0.08),
            0 0 0 2px rgba(0,0,0,0.6),
            inset 0 1px 0 rgba(255,255,255,0.14),
            inset 0 -1px 0 rgba(0,0,0,0.6),
            0 40px 90px rgba(0,0,0,0.55);
          aspect-ratio: 9/19.5;
          overflow: hidden;
        }
        .aot-phone-frame::before {
          content: ""; position: absolute; inset: 0;
          border-radius: 44px;
          background: linear-gradient(120deg, transparent 20%, rgba(255,255,255,0.06) 40%, transparent 60%);
          pointer-events: none;
        }
        /* Side buttons */
        .aot-phone-btn {
          position: absolute; background: linear-gradient(180deg, #1a1a20, #0a0a10);
          border-radius: 2px; z-index: 0;
        }
        .aot-phone-btn-r { right: -2px; top: 30%; width: 3px; height: 44px; box-shadow: inset -1px 0 0 rgba(255,255,255,0.15); }
        .aot-phone-btn-l1 { left: -2px; top: 22%; width: 3px; height: 22px; box-shadow: inset 1px 0 0 rgba(255,255,255,0.15); }
        .aot-phone-btn-l2 { left: -2px; top: 32%; width: 3px; height: 34px; box-shadow: inset 1px 0 0 rgba(255,255,255,0.15); }
        .aot-phone-btn-l3 { left: -2px; top: 44%; width: 3px; height: 34px; box-shadow: inset 1px 0 0 rgba(255,255,255,0.15); }

        .aot-phone-screen {
          position: relative;
          border-radius: 38px;
          background: linear-gradient(180deg, #0e1d42 0%, #0a1530 60%, #050b1e 100%);
          overflow: hidden;
          padding: 14px 12px 8px;
          height: 100%;
          box-shadow: inset 0 0 30px rgba(0,0,0,0.5);
        }
        .aot-phone-notch {
          position: absolute; top: 7px; left: 50%; transform: translateX(-50%);
          width: 32%; height: 16px; background: #000; border-radius: 12px;
          box-shadow: inset 0 0 4px rgba(255,255,255,0.06);
          z-index: 5;
        }
        .aot-phone-status {
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 8px 0 6px; margin-top: -2px; margin-bottom: 6px;
          color: #fff; font-size: 8px; font-weight: 600; letter-spacing: 0.02em;
          font-family: var(--font-heading);
          position: relative; z-index: 6;
        }
        .aot-status-icons { display: inline-flex; align-items: center; gap: 3px; }
        .aot-phone-content { position: relative; z-index: 2; padding-top: 4px; }
        .aot-phone-home {
          position: absolute; bottom: 6px; left: 50%; transform: translateX(-50%);
          width: 40%; height: 3px; border-radius: 2px;
          background: rgba(255,255,255,0.35);
        }
        .aot-intensity-bar {
          background: linear-gradient(90deg, #2a4470 0%, #eed89a 40%, #f6a26a 70%, #e46a5a 100%);
        }
        .aot-phone-reflection {
          position: absolute; left: 8%; right: 8%; bottom: -30px;
          height: 30px;
          background: radial-gradient(ellipse at center, rgba(238,216,154,0.20), transparent 75%);
          filter: blur(6px);
          transform: scaleY(0.5);
        }

        /* Sync flash on range change */
        .aot-phone-sync { animation: aotSyncFlash 0.9s ease-out; }
        .aot-sync-flash { animation: aotSyncFlashCard 0.9s ease-out; }
        @keyframes aotSyncFlash {
          0%   { filter: brightness(1) drop-shadow(0 0 0 rgba(238,216,154,0)); }
          40%  { filter: brightness(1.15) drop-shadow(0 0 24px rgba(238,216,154,0.6)); }
          100% { filter: brightness(1) drop-shadow(0 0 0 rgba(238,216,154,0)); }
        }
        @keyframes aotSyncFlashCard {
          0%   { box-shadow: 0 30px 80px rgba(4,10,30,0.4), 0 0 0 0 rgba(238,216,154,0); }
          40%  { box-shadow: 0 30px 80px rgba(4,10,30,0.4), 0 0 40px 4px rgba(238,216,154,0.35); }
          100% { box-shadow: 0 30px 80px rgba(4,10,30,0.4), 0 0 0 0 rgba(238,216,154,0); }
        }

        /* Chart hover scrubber pulse */
        .aot-scrubber-pulse {
          transform-box: fill-box;
          transform-origin: center;
          animation: aotScrubPulse 1.4s ease-in-out infinite;
        }
        @keyframes aotScrubPulse {
          0%,100% { opacity: 1; }
          50%     { opacity: 0.55; }
        }
        .aot-dash-chart { touch-action: none; }
        .aot-dash-card { transition: box-shadow 0.6s ease; }

        /* Biometric focus reveal — path draws in from left, base lines fade */
        .aot-focus-line { animation: aotFocusReveal 0.7s cubic-bezier(0.2,0.9,0.3,1) both; }        @keyframes aotFocusReveal {
          0%   { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .aot-line-base { transition: opacity 0.5s ease; }
        .aot-tile-active { animation: aotTileActive 0.6s cubic-bezier(0.2,0.9,0.3,1) both; }
        @keyframes aotTileActive {
          0%   { transform: scale(1); }
          50%  { transform: scale(1.03); }
          100% { transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .aot-focus-line, .aot-tile-active { animation: none !important; }
        }

        /* Theme Constellation */
        .aot-cn-bg-star {
          position: absolute; border-radius: 50%;
          background: radial-gradient(circle, rgba(255,255,255,0.9), rgba(255,255,255,0.15) 55%, transparent 80%);
          animation: aotCnStar 4s ease-in-out infinite;
          transform: translate(-50%, -50%);
        }
        @keyframes aotCnStar {
          0%, 100% { opacity: 0.2; transform: translate(-50%, -50%) scale(0.8); }
          50%      { opacity: 0.9; transform: translate(-50%, -50%) scale(1.1); }
        }
        .aot-cn-edge { transition: stroke 0.35s ease, stroke-width 0.35s ease, stroke-opacity 0.35s ease; }
        .aot-cn-node { cursor: pointer; }
        .aot-cn-glow {
          transform-box: fill-box; transform-origin: center;
          animation: aotCnGlow 5s ease-in-out infinite;
        }
        .aot-cn-glow-active {
          transform-box: fill-box; transform-origin: center;
          animation: aotCnGlowActive 1.6s ease-in-out infinite;
        }
        @keyframes aotCnGlow {
          0%, 100% { opacity: 0.55; transform: scale(0.9); }
          50%      { opacity: 0.9;  transform: scale(1.05); }
        }
        @keyframes aotCnGlowActive {
          0%, 100% { opacity: 0.95; transform: scale(1.08); }
          50%      { opacity: 0.6;  transform: scale(1.25); }
        }
        @media (prefers-reduced-motion: reduce) {
          .aot-cn-bg-star, .aot-cn-glow, .aot-cn-glow-active { animation: none !important; }
        }

        /* Constellation drawer loading shimmer */
        .aot-cn-shimmer {
          background: linear-gradient(90deg, rgba(238,216,154,0.05) 0%, rgba(238,216,154,0.35) 50%, rgba(238,216,154,0.05) 100%);
          background-size: 200% 100%;
          animation: aotCnShimmer 1.4s linear infinite;
        }
        @keyframes aotCnShimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @media (prefers-reduced-motion: reduce) { .aot-cn-shimmer { animation: none; } }

        /* Connection dot */
        .aot-connect-dot {
          box-shadow: 0 0 0 0 rgba(238,216,154,0.7);
          animation: aotConnectDot 2.2s ease-out infinite;
        }
        @keyframes aotConnectDot {
          0%   { box-shadow: 0 0 0 0 rgba(238,216,154,0.7); }
          70%  { box-shadow: 0 0 0 8px rgba(238,216,154,0); }
          100% { box-shadow: 0 0 0 0 rgba(238,216,154,0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .aot-scrubber-pulse, .aot-connect-dot, .aot-phone-sync, .aot-sync-flash,
          .aot-light-bridge, .aot-phone-halo { animation: none !important; }
        }

        /* Modal entrance */
        .modal-in { animation: modalIn 0.4s cubic-bezier(0.2,0.9,0.3,1) both; }
        @keyframes modalIn { 0% { opacity: 0; transform: translateY(20px) scale(0.98); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        @media (prefers-reduced-motion: reduce) { .modal-in { animation: none; } }
        .ana-scope .glass-strong{ background: rgba(9,26,66,0.4); border-color: rgba(45,212,191,0.22); backdrop-filter: blur(26px); box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 0 40px rgba(45,212,191,0.08) }
        .ana-scope .text-amber-300{ color:#fde68a !important }
        .ana-scope .gold-divider{ height:1px; width:100%; background:linear-gradient(90deg, transparent, rgba(251,191,36,0.55), transparent) }
        .ana-scope header{ background: rgba(10,18,48,0.6) !important; border-color: rgba(251,191,36,0.18) !important }

        /* ────── (b) Wake-up shimmer on section headings ────── */
        .ana-heading{
          background-image: linear-gradient(100deg,
            #ffffff 42%,
            rgba(255,255,255,0.55) 50%,
            #ffffff 58%
          );
          background-size: 260% 100%;
          background-position: 100% 0;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }
        .reveal.is-visible .ana-heading{
          animation: anaHeadingShimmer 1.7s cubic-bezier(0.22,1,0.36,1) 0.15s 1 forwards;
        }
        @keyframes anaHeadingShimmer{
          0%   { background-position: 100% 0; }
          100% { background-position: 0 0; }
        }
        @media (prefers-reduced-motion: reduce){
          .ana-heading{ background:none; -webkit-text-fill-color: initial; color:#fff; }
          .reveal.is-visible .ana-heading{ animation:none; }
        }

        /* ────── (d) Hairline gold underline that draws in ────── */
        .ana-heading{ position: relative; padding-bottom: 18px; }
        .ana-heading::after{
          content:""; position:absolute; left:0; bottom:0;
          height:1px; width:180px; max-width: 40%;
          background: linear-gradient(90deg, rgba(251,191,36,0.9), rgba(251,191,36,0.15) 70%, rgba(251,191,36,0));
          transform: scaleX(0); transform-origin: left center;
          transition: transform 1.2s cubic-bezier(0.65,0,0.35,1) 0.45s;
        }
        .ana-heading.centered::after{
          left:50%; transform: translateX(-50%) scaleX(0); transform-origin: center;
          background: linear-gradient(90deg, rgba(251,191,36,0) 0%, rgba(251,191,36,0.9) 50%, rgba(251,191,36,0) 100%);
        }
        .reveal.is-visible .ana-heading::after{ transform: scaleX(1); }
        .reveal.is-visible .ana-heading.centered::after{ transform: translateX(-50%) scaleX(1); }
        @media (prefers-reduced-motion: reduce){
          .ana-heading::after{ transition:none; transform: scaleX(1); }
          .ana-heading.centered::after{ transform: translateX(-50%) scaleX(1); }
        }
      `}</style>

      <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-white/5 bg-[#050b1e]/60">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-shrink-0">
            <Logo size="sm" />
          </div>
          <div className="hidden lg:flex items-center gap-1.5 text-[12px]">
            {[
              { id: "overview", label: "Overview" },
              { id: "nurse-experience", label: "Nurse Experience" },
              { id: "ana-integration", label: "ANA Integration" },
              { id: "privacy-doctrine", label: "Privacy & Doctrine" },
              { id: "ana-aot-section", label: "Progress Insights" },
              { id: "pilot-proposal", label: "Pilot" },
            ].map((n) => {
              const isActive = activeId === n.id;
              return (
                <a
                  key={n.id}
                  href={`#${n.id}`}
                  className={`px-3 py-1.5 rounded-full transition font-heading ${isActive ? "text-white bg-white/[0.10]" : "text-zinc-400 hover:text-white hover:bg-white/[0.06]"}`}
                  data-testid={`ana-nav-${n.id}`}
                  aria-current={isActive ? "true" : undefined}
                >{n.label}</a>
              );
            })}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setDocsOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border border-white/12 text-zinc-300 hover:text-white hover:border-amber-300/40 text-[12px] transition"
              data-testid="ana-docs-button"
            >
              <BookOpen size={12} /> Documents
            </button>
            <div className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-zinc-400 pl-3 border-l border-white/8">
              <Sparkles size={12} className="text-amber-300" /> ANA Concept Portal
            </div>
          </div>
        </div>
      </header>
      {docsOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-end" data-testid="ana-docs-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDocsOpen(false)} />
          <div className="relative w-full max-w-md h-full bg-[#050b1e] border-l border-white/10 shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
              <div className="flex items-center gap-2">
                <BookOpen size={16} className="text-amber-300" />
                <div className="font-heading text-white text-[15px]">Supporting Documents</div>
              </div>
              <button type="button" onClick={() => setDocsOpen(false)} className="text-zinc-400 hover:text-white" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-[12px] text-zinc-400 leading-relaxed mb-4">
                Supporting materials for ANA review. All documents are proposed frameworks, not final commitments.
              </p>
              {[
                { title: "The Future of Nurse Awareness", desc: "Vision paper", href: "/MMG-Future-of-Nurse-Awareness.pdf" },
                { title: "ANA Program Integration", desc: "Program pathways", href: "/MMG-ANA-Program-Integration.pdf" },
                { title: "Behavioral Support Doctrine", desc: "Doctrine framework", href: "/MMG-ANA-Behavioral-Support-Doctrine.pdf" },
              ].map((d) => (
                <a key={d.title} href={d.href} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] hover:border-amber-300/40 hover:bg-white/[0.05] p-4 transition group">
                  <FileText size={16} className="text-amber-300 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-white text-[14px] leading-tight group-hover:text-amber-200 transition">{d.title}</div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{d.desc}</div>
                  </div>
                  <ArrowUpRight size={14} className="text-zinc-500 group-hover:text-white transition mt-0.5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {jointOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" data-testid="ana-joint-discovery-panel">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setJointOpen(false)} />
          <div className="relative w-full max-w-xl rounded-3xl border border-amber-300/25 bg-[#0a1530] shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-7 py-5 border-b border-white/8">
              <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300">Next proposed step</div>
              <button type="button" onClick={() => setJointOpen(false)} className="text-zinc-400 hover:text-white" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="px-7 py-7">
              <h3 className="font-heading text-white text-2xl leading-tight">Joint Discovery</h3>
              <p className="mt-4 text-zinc-300/90 text-[14px] leading-relaxed">
                A focused working conversation could identify the priority nurse population, meaningful use
                cases, relevant ANA programs, privacy requirements, behavioral standards, technical
                considerations, and pilot success measures.
              </p>
              <p className="mt-4 text-zinc-400 text-[13px] italic leading-relaxed">
                The relationship and next steps would continue through direct discussion with ANA leadership.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => { setJointOpen(false); document.getElementById("pilot-proposal")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-gradient-to-b from-[#F4EAC8] to-[#E6D19E] text-[#0F1E44] font-heading font-medium text-sm shadow-[0_10px_24px_rgba(238,216,154,0.28)]"
                  data-testid="joint-review-pilot"
                >
                  Review the Proposed Pilot <ArrowRight size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setJointOpen(false)}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-zinc-300 hover:text-white text-sm transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 1. HERO */}
      <section id="overview" className="relative pt-20 pb-24 sm:pt-28 sm:pb-32 px-6 sm:px-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-[60vw] h-[60vw] rounded-full bg-blue-500/15 blur-[160px]" />
          <div className="absolute top-10 right-0 w-[45vw] h-[45vw] rounded-full bg-sky-400/12 blur-[160px]" />
          <div className="absolute bottom-0 left-1/3 w-[35vw] h-[35vw] rounded-full bg-amber-300/8 blur-[140px]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal is-visible max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-[11px] uppercase tracking-[0.28em] text-zinc-300 mb-4">
              <HeartPulse size={12} className="text-rose-300" /> For the American Nurses Association
            </div>
            <p className="font-body text-[12.5px] uppercase tracking-[0.22em] text-teal-300/80 mb-2" data-testid="ana-hero-label">
              A Private Awareness Platform for Nurses
            </p>
            <p className="font-body text-[13px] text-zinc-400 leading-relaxed mb-8 max-w-2xl" data-testid="ana-portal-separation-note">
              A private concept environment prepared specifically for ANA. This portal presents the proposed
              nurse experience, program integration, privacy architecture, behavioral doctrine, and pilot
              pathway separately from investor materials.
            </p>
            <h1 className="ana-heading font-heading font-medium text-5xl sm:text-6xl lg:text-7xl text-white tracking-tight leading-[1.02] text-balance">
              Awareness in the moment. <span className="italic">Evolution over time.</span>
            </h1>
            <p className="mt-6 max-w-2xl font-body text-lg sm:text-xl text-zinc-300/90 leading-relaxed text-balance">
              My Mood Groove is a private awareness and engagement platform designed to help nurses understand
              what they are carrying, recognize what they may need, and connect with relevant support at the
              moment it matters.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a href="#nurse-experience"
                 className="btn-premium inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-white text-black font-heading font-medium text-sm tracking-tight" data-testid="ana-hero-primary">
                <ArrowUpRight size={14} /> Explore the Nurse Experience
              </a>
              <a href="#ana-integration"
                 className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 glass-strong text-white font-heading font-medium text-sm tracking-tight hover:bg-white/10 transition" data-testid="ana-hero-secondary">
                <ArrowUpRight size={14} /> See How It Connects to ANA
              </a>
              <a href="#behavioral-doctrine"
                 className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 glass-strong text-white font-heading font-medium text-sm tracking-tight hover:bg-white/10 transition" data-testid="ana-hero-tertiary">
                <FileText size={14} /> Review the Behavioral Doctrine
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 2. THE EMOTIONAL LANDSCAPE */}
      <section id="the-need" className="relative py-20 px-6 sm:px-10">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-3">The nurse experience</div>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight text-balance">
              No two nurses carry the same day home.
            </h2>
            <p className="mt-5 font-body text-zinc-300/90 leading-relaxed">
              A difficult shift may leave one nurse exhausted, another grieving, another angry, and another
              questioning whether they can continue. Their physiological signals may appear similar. Their
              emotional realities may be entirely different.
            </p>
            <p className="mt-4 font-body text-zinc-300/90 leading-relaxed">
              My Mood Groove creates a private space where each nurse can pause, express what happened,
              recognize what they are carrying, and decide what may help next.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {landscape.map(({ icon: Icon, title, text }, i) => (
              <div key={title} className="reveal glass rounded-2xl p-6 sm:p-7 flex items-start gap-4" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`ana-landscape-${i}`}>
                <span className="shrink-0 w-11 h-11 rounded-xl bg-white/[0.05] border border-teal-300/25 flex items-center justify-center">
                  <Icon size={20} className="text-teal-300" />
                </span>
                <div>
                  <div className="font-heading text-white text-[15px] leading-tight mb-1.5">{title}</div>
                  <p className="text-[14px] text-zinc-300/90 leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="reveal mt-8 font-heading text-white text-lg italic leading-snug max-w-3xl">
            The platform does not decide what a nurse feels or needs. It helps the nurse recognize it.
          </p>
        </div>
      </section>

      {/* 3. THE MISSING AWARENESS LAYER */}
      <section className="relative py-24 px-6 sm:px-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 -left-20 w-[45vw] h-[45vw] rounded-full bg-blue-500/10 blur-[140px]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-3">The missing layer</div>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight text-balance">
              The next frontier of digital health is meaning.
            </h2>
            <p className="mt-4 font-body text-zinc-400 leading-relaxed">
              Digital health has become increasingly effective at measuring activity and physiology. The next
              frontier is helping people understand the personal meaning surrounding those signals.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {generations.map((g, i) => (
              <div key={g.title} className={`reveal glass rounded-2xl p-7 ${i === 2 ? "border-white/25 bg-white/[0.06]" : ""}`} style={{ transitionDelay: `${i * 80}ms` }} data-testid={`ana-gen-${i}`}>
                <div className="font-heading text-2xl text-white font-medium mb-2">{g.title}</div>
                <p className="text-sm text-zinc-300/85 leading-relaxed">{g.body}</p>
              </div>
            ))}
          </div>
          <p className="reveal mt-6 max-w-3xl font-body text-zinc-300/90 italic leading-relaxed">
            A wearable may detect that something changed. My Mood Groove helps the nurse explore why it may
            have changed and what response may be useful.
          </p>

          {/* Comparison table */}
          <div className="reveal mt-6 glass rounded-2xl overflow-hidden">
            <div className="grid grid-cols-2 text-[10px] uppercase tracking-[0.24em] border-b border-white/10">
              <div className="px-5 sm:px-7 py-3 text-zinc-400">ANA programs provide</div>
              <div className="px-5 sm:px-7 py-3 text-teal-300 border-l border-white/10">My Mood Groove adds</div>
            </div>
            {comparison.map(([today, adds], i) => (
              <div key={i} className={`grid grid-cols-2 items-center ${i % 2 ? "bg-white/[0.015]" : ""}`} data-testid={`compare-row-${i}`}>
                <div className="px-5 sm:px-7 py-3.5 text-sm text-zinc-400 leading-snug">{today}</div>
                <div className="px-5 sm:px-7 py-3.5 text-sm text-zinc-100 leading-snug border-l border-white/10 flex items-start gap-2">
                  <ArrowRight size={14} className="text-teal-300 mt-0.5 shrink-0" />
                  <span>{adds}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="reveal mt-6 max-w-3xl font-body text-zinc-300 leading-relaxed">
            My Mood Groove does not replace ANA programs. It helps nurses experience their value personally,
            privately, and at the moment support is most relevant.
          </p>
        </div>
      </section>

      {/* 4. THE AWARENESS WINDOW */}
      <section className="relative py-24 px-6 sm:px-10">
        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="reveal mb-10 text-center">
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-3">The awareness window</div>
            <h2 className="ana-heading centered font-heading font-medium text-3xl sm:text-4xl text-white tracking-tight leading-tight text-balance">
              Recognition can change the trajectory.
            </h2>
          </div>
          <div className="space-y-2">
            {cascade.map((line, i) => {
              const isWindow = line.includes("AWARENESS WINDOW");
              return (
                <div key={i}
                  className={`reveal rounded-xl px-5 py-3 ${
                    isWindow ? "ana-window-pulse bg-gradient-to-r from-teal-400/25 to-sky-400/25 border border-teal-300/50 text-teal-50 font-heading tracking-wide text-center" :
                    "glass text-zinc-200"
                  } text-sm sm:text-base`}
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  {line}
                </div>
              );
            })}
          </div>
          <p className="reveal mt-8 text-center max-w-2xl mx-auto font-body text-zinc-300 leading-relaxed">
            My Mood Groove is designed to meet the nurse earlier, while there is still room to reflect,
            regulate, seek support, and choose a meaningful next step.
          </p>
          <p className="reveal mt-3 text-center max-w-2xl mx-auto font-body text-zinc-500 text-xs italic">
            Illustrative progression, not a predicted path for any individual.
          </p>
        </div>
      </section>

      {/* 5. THE BIOMETRIC BRIDGE */}
      <section className="relative py-24 px-6 sm:px-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-0 w-[42vw] h-[42vw] rounded-full bg-sky-500/10 blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[34vw] h-[34vw] rounded-full bg-violet-500/10 blur-[140px]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal grid lg:grid-cols-[1fr_0.85fr] gap-8 lg:gap-12 items-center mb-14">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.28em] text-teal-300 mb-3">The biometric bridge</div>
              <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight text-balance">
                From physiological signals to personal understanding.
              </h2>
              <p className="mt-4 font-body text-zinc-300/90 leading-relaxed">
                Wearables can reveal changes in heart rate, HRV, sleep, recovery, activity, and stress indicators.
                They cannot determine what those signals mean in a person's life.
              </p>
              <p className="mt-3 font-body text-zinc-300/90 leading-relaxed">
                With the nurse's permission, My Mood Groove can pair selected wearable signals with private
                reflection — helping the nurse explore what may be contributing to a change and what response
                may be useful.
              </p>
            </div>
            <div className="relative h-[260px] sm:h-[320px]" aria-hidden="true">
              <img
                src="/wearable-watch.png"
                alt="Smartwatch showing a heart-rate waveform"
                className="mmg-wear mmg-wear-a absolute right-2 top-1 w-[52%] select-none pointer-events-none"
              />
              <img
                src="/wearable-oura.png"
                alt="Silver smart ring"
                className="mmg-wear mmg-wear-b absolute left-0 bottom-0 w-[46%] select-none pointer-events-none"
              />
            </div>
          </div>
          <BiometricBridge />

          {/* Scientific rationale — the working hypothesis */}
          <div className="reveal mt-14 max-w-4xl mx-auto" data-testid="ana-thesis-card">
            <div className="relative rounded-3xl border border-amber-300/25 bg-gradient-to-br from-[rgba(238,216,154,0.06)] via-[rgba(20,26,55,0.35)] to-[rgba(6,12,28,0.45)] backdrop-blur-xl px-7 sm:px-10 py-9 sm:py-11 overflow-hidden">
              <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-amber-300/12 blur-3xl pointer-events-none" aria-hidden />
              <div className="relative">
                <div className="text-[10.5px] uppercase tracking-[0.32em] text-amber-300/85 mb-5 text-center">
                  The working hypothesis
                </div>
                <div className="mx-auto mb-5 h-px w-16 bg-gradient-to-r from-transparent via-amber-300/60 to-transparent" aria-hidden />
                <blockquote className="font-heading font-light text-white italic text-lg sm:text-[22px] leading-[1.45] tracking-tight text-balance text-center">
                  &ldquo;Longitudinal integration of multimodal biometric signals with activity, location,
                  environmental context, life domain, cultural context, and repeated first-person emotional
                  interpretation will produce more accurate and personally meaningful models of physiological
                  and emotional state than population-based biometric classification alone.&rdquo;
                </blockquote>
                <div className="mx-auto mt-6 h-px w-16 bg-gradient-to-r from-transparent via-amber-300/40 to-transparent" aria-hidden />
                <p className="mt-5 text-[11px] uppercase tracking-[0.28em] text-amber-200/75 text-center">
                  The scientific foundation for everything we're building.
                </p>
                <p className="mt-2.5 text-[11px] font-heading italic text-zinc-400/80 tracking-tight text-center">
                  — My Mood Groove working thesis, 2026
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. INTERACTIVE ANA PROGRAM INTEGRATION */}
      <section id="nurse-experience" className="relative py-24 px-6 sm:px-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[40vw] h-[40vw] rounded-full bg-amber-400/8 blur-[140px]" />
          <div className="absolute bottom-0 left-0 w-[35vw] h-[35vw] rounded-full bg-blue-500/12 blur-[140px]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal text-center max-w-3xl mx-auto mb-10">
            <div className="text-[11px] uppercase tracking-[0.28em] text-teal-300 mb-3">The nurse experience</div>
            <h2 className="ana-heading centered font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight text-balance">
              One private experience. Multiple pathways into ANA support.
            </h2>
            <p className="mt-5 font-body text-zinc-200 leading-relaxed max-w-2xl mx-auto">
              My Mood Groove helps a nurse recognize what may be needed in the moment, then makes the most
              relevant ANA program, resource, or support pathway easier to reach.
            </p>
            <p className="mt-3 font-body text-zinc-300/90 leading-relaxed max-w-2xl mx-auto text-[15px]">
              It does not replace ANA programs. It helps make them more personal, timely, and usable.
            </p>
            <p className="mt-4 font-heading text-white text-lg italic leading-snug max-w-2xl mx-auto">
              My Mood Groove could serve as a private awareness and navigation layer across the ANA ecosystem.
            </p>
            <p className="mt-3 font-body text-zinc-500 text-[11.5px] italic leading-relaxed max-w-2xl mx-auto">
              The American Nurses Foundation is a distinct charitable organization from ANA and ANCC.
            </p>
            <p className="mt-6 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-amber-300/90">
              <ArrowDown size={13} /> Select a program below to see how My Mood Groove could bring it into a nurse's daily experience
            </p>
          </div>
          <div id="ana-integration" className="scroll-mt-[88px]">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-amber-300/90">
                <span className="h-px w-8 bg-amber-300/30" />
                ANA Program Integration
                <span className="h-px w-8 bg-amber-300/30" />
              </div>
              <p className="mt-2 text-zinc-400 text-[12.5px] max-w-xl mx-auto leading-relaxed">
                Five programs. Each shown with what it is, what it accomplishes, how My Mood Groove could connect, and a moment in the nurse's experience.
              </p>
            </div>
            <ProgramMindMap />
          </div>
        </div>
      </section>

      {/* 7. VALUE AT THREE LEVELS */}
      <section className="relative py-24 px-6 sm:px-10">
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-2xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-3">Value at three levels</div>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight text-balance">
              Value for the nurse, participating organizations, and ANA.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: HeartPulse, tag: "Nurse",
                body: "A private place to decompress, reflect, gain awareness, and identify a meaningful next step without feeling watched, scored, or evaluated.",
                emphasis: true },
              { icon: Users, tag: "Participating organizations",
                body: "Privacy-protected understanding of broad workforce pressures, resource needs, participation patterns, and opportunities for earlier support without exposing individual employees." },
              { icon: TrendingUp, tag: "ANA",
                body: "A continuing connection with nurses between surveys, courses, assessments, and formal initiatives, while preserving clear privacy boundaries and strengthening access to relevant programs." },
            ].map(({ icon: Icon, tag, body, emphasis }, i) => (
              <div key={tag} className={`reveal rounded-2xl p-7 ${emphasis ? "border border-amber-300/35 bg-gradient-to-br from-amber-300/[0.07] to-transparent md:col-span-3 md:p-9" : "glass"}`} style={{ transitionDelay: `${i * 80}ms` }}>
                <Icon size={emphasis ? 26 : 22} className="text-amber-300 mb-4" />
                <div className={`text-[11px] uppercase tracking-[0.24em] mb-2 ${emphasis ? "text-amber-200" : "text-zinc-400"}`}>For the {tag.toLowerCase()}</div>
                <p className={`text-zinc-200 leading-relaxed ${emphasis ? "text-lg" : ""}`}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. AWARENESS OVER TIME */}
      <AwarenessOverTime />

      {/* 8. PRIVACY-FIRST ARCHITECTURE */}
      <section id="privacy-doctrine" className="relative py-24 px-6 sm:px-10">
        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="reveal glass-strong rounded-3xl p-10 sm:p-14">
            <Shield size={28} className="text-emerald-300 mb-4" />
            <div className="text-[11px] uppercase tracking-[0.28em] text-emerald-300 mb-3">Privacy-first architecture</div>
            <h2 className="ana-heading font-heading font-medium text-3xl sm:text-4xl text-white tracking-tight leading-tight text-balance">
              Trust is not a feature. Trust is the foundation.
            </h2>
            <ul className="mt-8 space-y-5 font-body">
              {[
                { title: "Private conversations remain private",
                  body: "Individual conversations are not shared with employers or ANA as transcripts." },
                { title: "Participation is voluntary",
                  body: "The nurse chooses whether to use the platform, connect a wearable, save information, or share selected signals." },
                { title: "Organizational insight is limited",
                  body: "Any organizational reporting must be consent-based, deidentified, aggregated, and subject to appropriate minimum population thresholds." },
                { title: "Personal data is not used against the nurse",
                  body: "Biometric, conversational, or emotional information is not used for discipline, productivity scoring, credentialing, fitness for duty, insurance, or employment decisions." },
                { title: "The nurse can review and change permissions",
                  body: "Consent choices should be understandable, reversible, and accessible at any time." },
              ].map((t) => (
                <li key={t.title} className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-300 shrink-0" />
                  <div>
                    <div className="font-heading text-white text-[15px] leading-tight mb-1">{t.title}</div>
                    <p className="text-[14px] text-zinc-300/90 leading-relaxed">{t.body}</p>
                  </div>
                </li>
              ))}
            </ul>

            {/* Insight flow diagram */}
            <div className="reveal mt-10 pt-8 border-t border-white/10" data-testid="privacy-flow">
              <div className="text-[11px] uppercase tracking-[0.28em] text-emerald-300 mb-6">
                How insight flows without compromising privacy
              </div>
              <div className="flex flex-col items-stretch">
                {[
                  { icon: Lock, title: "Private nurse experience",
                    body: "Conversations and reflections remain with the nurse.",
                    gate: "only with consent" },
                  { icon: Layers, title: "Optional eligible signals",
                    body: "With clear consent, selected categories and outcome signals may be stripped of personal identity.",
                    gate: "privacy threshold review" },
                  { icon: Shield, title: "Privacy threshold review",
                    body: "Only signals meeting established privacy thresholds may proceed.",
                    gate: "only after minimum population thresholds" },
                  { icon: Users, title: "Aggregated organizational patterns",
                    body: "Only group-level trends meeting defined privacy thresholds may be reported.",
                    gate: "" },
                  { icon: LineChart, title: "Program improvement",
                    body: "Patterns may help inform resource planning and program design — never individual evaluation.",
                    gate: null },
                ].map((s, i, arr) => (
                  <React.Fragment key={s.title}>
                    <div className="flex items-start gap-4 rounded-2xl bg-white/[0.03] border border-emerald-300/15 p-4 sm:p-5">
                      <span className="shrink-0 w-10 h-10 rounded-xl bg-emerald-400/10 border border-emerald-300/25 flex items-center justify-center">
                        <s.icon size={18} className="text-emerald-300" />
                      </span>
                      <div>
                        <div className="font-heading text-white text-[15px] leading-tight">{s.title}</div>
                        <p className="text-[13px] text-zinc-400 leading-relaxed mt-1">{s.body}</p>
                      </div>
                    </div>
                    {i < arr.length - 1 && (
                      <div className="flex items-center gap-3 py-2 pl-5">
                        <span className="priv-flow-arrow flex items-center justify-center w-6 h-6 rounded-full bg-emerald-400/15">
                          <ArrowDown size={14} className="text-emerald-300" />
                        </span>
                        {s.gate && (
                          <span className="text-[11px] italic text-emerald-200/80">{s.gate}</span>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <p className="mt-6 text-[13px] text-zinc-300 leading-relaxed max-w-3xl">
                <span className="font-heading text-white">Deidentification alone is not sufficient.</span>{" "}
                Any eligible signals would also need to meet defined aggregation, minimum population, access
                control, retention, and reidentification risk standards established during pilot design.
              </p>
              <p className="mt-6 text-[12px] text-zinc-400 italic leading-relaxed">
                The proposed architecture is designed so that ANA and participating organizations do not
                receive individual conversations, personal biometric records, identities, or emotional
                profiles. Final safeguards would be established through pilot agreements, legal review,
                privacy review, technical validation, and independent governance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section bridge → Behavioral Doctrine */}

      {/* 9. THE BEHAVIORAL DOCTRINE */}
      <section id="behavioral-doctrine" className="relative pt-16 pb-16 px-6 sm:px-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-0 w-[40vw] h-[40vw] rounded-full bg-teal-400/8 blur-[150px]" />
          <div className="absolute bottom-0 right-1/4 w-[34vw] h-[34vw] rounded-full bg-violet-500/10 blur-[140px]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-2xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.28em] text-teal-300 mb-3">The behavioral doctrine</div>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight text-balance">
              What would ANA require before trusting an AI-supported experience with nurses?
            </h2>
            <p className="mt-4 font-body text-zinc-300 leading-relaxed">
              That is the right question. My Mood Groove is designed doctrine-first so that trust can be
              examined, tested, and earned rather than assumed. Before the platform responds, its behavior
              is governed by reviewable principles defining how it listens, preserves nurse agency, recognizes
              emotional intensity, distinguishes personal strain from organizational problems, identifies safety
              concerns, and connects people with qualified human support.
            </p>
            <p className="mt-4 font-body text-zinc-400 text-[13.5px] leading-relaxed">
              The proposed Behavioral Doctrine would be informed by established approaches including
              person-centered listening, Motivational Interviewing, trauma-informed care, Psychological
              First Aid, Stress First Aid, cognitive and acceptance-based reflection, and relevant ANA
              nurse wellbeing frameworks. Each principle would be translated into testable conversational
              rules, nurse scenarios, review standards, escalation requirements, and audit criteria.
            </p>
          </div>
          <Doctrine />

          {/* Behavioral Boundary — six declarative lines */}
          <div className="reveal mt-14 rounded-3xl border border-amber-300/25 bg-gradient-to-br from-[#0e1d42]/60 to-[#0b1738]/60 p-8 sm:p-10" data-testid="ana-behavioral-boundary">
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-5">The Behavioral Boundary</div>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl">
              {[
                "Recognition is not diagnosis.",
                "Support is not treatment.",
                "Reflection is not clinical assessment.",
                "Guidance is not command.",
                "Awareness is not surveillance.",
                "Connection is not dependency.",
              ].map((line) => (
                <div key={line} className="flex items-start gap-2.5">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-amber-300 shrink-0" />
                  <p className="font-heading text-white text-[15px] leading-snug">{line}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-zinc-300/85 text-[13.5px] italic leading-relaxed max-w-2xl">
              A doctrine defines the behavioral boundary. Governance makes that boundary visible, testable,
              reviewable, and accountable.
            </p>
          </div>
        </div>
      </section>

      {/* 10. PRACTICAL PILOT PATHWAY (merged) */}
      <section id="pilot-proposal" className="relative py-24 px-6 sm:px-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-0 w-[45vw] h-[45vw] rounded-full bg-blue-500/10 blur-[140px]" />
          <div className="absolute top-1/4 right-0 w-[40vw] h-[40vw] rounded-full bg-teal-400/10 blur-[150px]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="reveal max-w-3xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-3">A practical next step</div>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight text-balance">
              Begin small. Learn honestly. Earn confidence before scale.
            </h2>
            <p className="mt-5 font-body text-zinc-300/90 leading-relaxed">
              The proposed next step is a focused discovery and pilot process designed with ANA, tested with
              participating nurses, and governed by clear privacy, behavioral, clinical, and safety standards.
              The purpose is not to confirm a predetermined conclusion. It is to determine whether the
              experience is trusted, useful, safe, relevant, and worthy of further development.
            </p>
          </div>

          {/* Three phases */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {pilotPhases.map((p, i) => (
              <div key={p.n} className="reveal glass rounded-2xl p-7" style={{ transitionDelay: `${i * 80}ms` }} data-testid={`pilot-phase-${i}`}>
                <div className="text-[11px] uppercase tracking-[0.24em] text-teal-300 mb-3">{p.n}</div>
                <div className="font-heading text-white text-lg leading-tight mb-2">{p.title}</div>
                <p className="text-[13.5px] text-zinc-300 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>

          {/* Pilot roles */}
          <div className="reveal glass-strong rounded-3xl p-8 sm:p-10 mb-6">
            <div className="text-[11px] uppercase tracking-[0.28em] text-teal-300 mb-6">Pilot roles</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {pilotRoles.map(({ icon: Icon, label, value }) => (
                <div key={label} className="rounded-2xl bg-white/[0.03] border border-white/10 p-5" data-testid={`pilot-role-${label}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={15} className="text-teal-300" />
                    <span className="text-[11px] uppercase tracking-[0.2em] text-teal-300">{label}</span>
                  </div>
                  <p className="text-[13.5px] text-zinc-200 leading-relaxed">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Pilot measurements — grouped */}
          <div className="reveal glass rounded-3xl p-8 sm:p-10">
            <div className="text-[11px] uppercase tracking-[0.28em] text-teal-300 mb-5">Pilot measurements</div>
            <div className="grid md:grid-cols-2 gap-6">
              {pilotMeasurementGroups.map((g) => (
                <div key={g.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`pilot-measure-group-${g.title.split(" ")[0].toLowerCase()}`}>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-amber-200 mb-3">{g.title}</div>
                  <div className="space-y-2">
                    {g.items.map((m) => (
                      <div key={m} className="flex items-start gap-2.5">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal-300 shrink-0" />
                        <p className="text-[13.5px] text-zinc-200 leading-relaxed">{m}</p>
                      </div>
                    ))}
                  </div>
                  {g.note && (
                    <p className="mt-3 text-[11.5px] text-zinc-400 italic leading-relaxed">{g.note}</p>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-8 text-[14px] text-zinc-300/90 italic leading-relaxed max-w-3xl">
              The pilot should measure whether the experience earns trust, not merely whether people use it.
            </p>
          </div>
        </div>
      </section>

      {/* 11. TEN COMMITMENTS */}
      <section className="relative py-24 px-6 sm:px-10">
        <div className="relative z-10 max-w-5xl mx-auto">
          <div className="reveal max-w-2xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-3">Ten commitments</div>
            <h2 className="ana-heading font-heading font-medium text-4xl sm:text-5xl text-white tracking-tight leading-tight text-balance">
              Commitments that guide every decision.
            </h2>
          </div>
          <div className="reveal text-[11px] uppercase tracking-[0.22em] text-teal-300 mb-3">Foundational commitments</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {foundationalCommitments.map((p, i) => (
              <div key={p} className="reveal rounded-2xl p-7 bg-gradient-to-br from-teal-500/12 to-sky-500/8 border border-teal-300/30" style={{ transitionDelay: `${i * 60}ms` }}>
                <div className="text-[11px] uppercase tracking-[0.24em] text-teal-300 mb-3">Foundational · {String(i + 1).padStart(2, "0")}</div>
                <p className="font-heading text-white text-lg leading-snug">{p}</p>
              </div>
            ))}
          </div>
          <div className="reveal text-[11px] uppercase tracking-[0.22em] text-amber-300 mb-3">Operating principles</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {operatingPrinciples.map((p, i) => (
              <div key={p} className="reveal flex items-start gap-4 px-5 py-4 rounded-xl bg-white/[0.03] border border-white/10" style={{ transitionDelay: `${i * 40}ms` }}>
                <span className="font-heading text-xs text-amber-300 tracking-[0.2em] mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-zinc-300 leading-relaxed">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing reflection — a quiet moment before the ask */}
      <section className="relative py-14 px-6 sm:px-10" data-testid="ana-closing-reflection">
        <div className="max-w-3xl mx-auto text-center">
          <div className="reveal">
            <div className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-amber-300/40 text-amber-300 text-lg mb-6 mx-auto" aria-hidden>◈</div>
            <p className="font-heading text-white text-xl sm:text-2xl leading-[1.55] tracking-tight text-balance">
              Technology should never replace human understanding. It should deepen it.
            </p>
            <p className="mt-6 font-body text-zinc-300/90 text-[15.5px] sm:text-base leading-relaxed text-balance">
              My Mood Groove was created with the belief that when people become more aware of what is happening
              within themselves, they make healthier decisions, strengthen relationships, and build more
              resilient organizations.
            </p>
            <p className="mt-5 font-heading italic text-amber-100/90 text-lg leading-relaxed text-balance">
              We believe every meaningful partnership begins the same way our conversations do:
              <span className="not-italic text-white"> by listening first.</span>
            </p>
            <p className="mt-7 font-heading text-[11px] uppercase tracking-[0.32em] text-zinc-400" data-testid="ana-closing-signature">
              — The Mood Groove Team
            </p>
          </div>
        </div>
      </section>

      {/* 11.5 SANCTUARY ROOM · FUTURE FORWARD */}
      <section id="sanctuary-room" className="ana-sanctuary relative overflow-hidden" data-testid="ana-sanctuary">
        {/* Atmospheric video background */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            data-testid="sanctuary-video"
          >
            <source src="/investor/atmospheric-loop.webm" type="video/webm" />
            <source src="/investor/atmospheric-loop.mp4" type="video/mp4" />
          </video>
          {/* Directional overlay — text stays legible on the left, atmosphere breathes on the right */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(3,6,15,0.72) 0%, rgba(3,6,15,0.40) 45%, rgba(3,6,15,0.18) 75%, rgba(3,6,15,0.35) 100%)",
            }}
          />
          {/* Soft top/bottom fades so the section reads as a chapter */}
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#03060f] to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#03060f]" />
          {/* Drifting particles — a light sprinkle of magic */}
          <div className="sanctuary-particles absolute inset-0">
            {Array.from({ length: 26 }).map((_, i) => (
              <span
                key={i}
                className="sanctuary-mote"
                style={{
                  left: `${(i * 37.3) % 100}%`,
                  top: `${(i * 21.7) % 100}%`,
                  animationDelay: `${(i * 0.5) % 12}s`,
                  animationDuration: `${14 + ((i * 3) % 10)}s`,
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 py-28 sm:py-36">
          <div className="reveal max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-[10.5px] uppercase tracking-[0.32em] text-amber-200/85 mb-8">
              <Sparkles size={12} className="text-amber-300" />
              Looking further ahead
            </div>
            <h2 className="hero-title font-heading font-light text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance">
              A sanctuary <span className="italic font-medium">between shifts.</span>
            </h2>
            <p className="hero-copy mt-6 font-body text-zinc-100/95 text-lg leading-relaxed max-w-2xl">
              The app is where the practice begins. The architecture continues into the room.
              We imagine dedicated spaces — inside hospitals, associations, and workplace campuses —
              where the same awareness experience becomes ambient. A place a nurse can step into for
              seven quiet minutes and step out of a little more themselves.
            </p>

            {/* Feature triptych */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  Icon: Projector,
                  tag: "Atmospheric projection",
                  head: "Walls that respond",
                  text: "Soft projection of light, texture, and slow motion — tuned to the moment the nurse arrives in.",
                },
                {
                  Icon: Wind,
                  tag: "Sound & scent",
                  head: "Guided by the senses",
                  text: "Curated soundscapes paired with optional accord-based diffusion (calm, clarity, ground). Never medical claims — sensory context.",
                },
                {
                  Icon: Home,
                  tag: "The Sanctuary Room",
                  head: "A dedicated space to reset",
                  text: "A quiet, hospital-hosted room for private decompression between shifts, after a difficult event, or before returning.",
                },
              ].map(({ Icon, tag, head, text }) => (
                <div
                  key={tag}
                  className="sanctuary-card group relative rounded-2xl border border-white/15 bg-white/[0.06] backdrop-blur-xl p-5 overflow-hidden transition duration-500 hover:border-amber-300/40 hover:bg-white/[0.09]"
                  data-testid={`sanctuary-card-${tag.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {/* Card halo */}
                  <div className="sanctuary-halo absolute -top-1/2 -right-1/2 w-[140%] h-[140%] rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition duration-700" aria-hidden />
                  <div className="relative z-10 flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-amber-300/40 bg-amber-300/10 text-amber-200">
                      <Icon size={14} />
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.24em] text-amber-200/80">{tag}</span>
                  </div>
                  <div className="relative z-10 font-heading text-white text-[15.5px] leading-tight">{head}</div>
                  <p className="relative z-10 mt-1.5 text-zinc-200/85 text-[13px] leading-relaxed">{text}</p>
                </div>
              ))}
            </div>

            {/* Envisioned use */}
            <div className="mt-10 rounded-2xl border border-white/12 bg-white/[0.04] backdrop-blur-xl px-6 py-5 max-w-2xl">
              <div className="text-[10.5px] uppercase tracking-[0.28em] text-amber-200/80 mb-2">Envisioned use</div>
              <p className="text-zinc-100/95 text-[14.5px] leading-relaxed">
                A nurse steps in after a hard admission. The room reads the pace of the door opening
                and settles the light. A private minute becomes a short breathing sequence, an
                Awareness Signal, and a quiet return. No sign-in. No screen. Just atmosphere.
              </p>
            </div>

            <p className="mt-8 text-[11.5px] text-zinc-300/75 italic leading-relaxed max-w-2xl">
              Concept forward. Not part of the initial pilot scope. Shared as a natural extension
              of the awareness architecture — how the same doctrine can reach past the phone and
              into the physical spaces where nurses actually spend their days.
            </p>
          </div>
        </div>
      </section>

      {/* 12. FINAL CTA */}
      <section className="relative py-24 px-6 sm:px-10">
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="reveal glass-strong rounded-3xl p-10 sm:p-14">
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-300 mb-4">Ready to explore together</div>
            <h2 className="ana-heading centered font-heading font-medium text-3xl sm:text-4xl text-white tracking-tight leading-tight text-balance">
              An opportunity to establish a responsible new standard for AI supported nurse awareness — <span className="italic">shaped with nurses and guided by ANA.</span>
            </h2>
            <div className="mt-10 flex flex-col items-center gap-5">
              <button
                type="button"
                data-testid="cta-advance-joint-discovery"
                onClick={() => setJointOpen(true)}
                className="group inline-flex items-center gap-2.5 rounded-full px-8 py-3.5 bg-gradient-to-r from-teal-500 to-sky-500 text-white font-heading font-medium text-base tracking-tight shadow-[0_0_32px_rgba(45,212,191,0.4)] hover:shadow-[0_0_48px_rgba(45,212,191,0.55)] transition"
              >
                Advance to Joint Discovery
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="/MMG-Future-of-Nurse-Awareness.pdf" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 glass-strong text-zinc-200 font-heading font-medium text-sm tracking-tight hover:bg-white/10 transition" data-testid="cta-pdf-vision">
                  <FileText size={15} /> Read the Vision Paper
                </a>
                <a href="/MMG-ANA-Program-Integration.pdf" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 glass-strong text-zinc-200 font-heading font-medium text-sm tracking-tight hover:bg-white/10 transition" data-testid="cta-pdf-integration">
                  <Download size={15} /> Review Program Integration
                </a>
                <a href="/MMG-ANA-Behavioral-Support-Doctrine.pdf" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 glass-strong text-zinc-200 font-heading font-medium text-sm tracking-tight hover:bg-white/10 transition" data-testid="cta-pdf-doctrine">
                  <FileText size={15} /> Explore the Behavioral Doctrine
                </a>
              </div>
            </div>
          </div>
          <p className="mt-10 text-zinc-500 text-xs italic max-w-2xl mx-auto leading-relaxed">
            Concept materials prepared for discussion with the American Nurses Association. ANA program names and
            trademarks remain the property of their respective owners. No partnership, endorsement, or formal
            affiliation is implied unless established in writing.
          </p>
          <p className="mt-4 text-[10.5px] uppercase tracking-[0.22em] text-amber-200/60 font-body" data-testid="patent-pending-note">
            My Mood Groove · U.S. Patent Pending · Confirmed August 2026
          </p>
        </div>
      </section>
    </div>
  );
}
